import { apiSlice as api } from './'

export const addTagTypes = [
  'Assessment',
  'AssessmentScenario',
  'BackgroundProcesses',
  'Baseline',
  'BaselineRecalculation',
  'BaseSettings',
  'ClassLocation',
  'ColourByValue',
  'ConsequenceArea',
  'CustomView',
  'DataManagement',
  'DataUpdate',
  'Esri',
  'GasSystem',
  'GroupType',
  'Job',
  'Operator',
  'PhmsaExport',
  'QraDataTable',
  'QraDataTableDrillDown',
  'QraGraph',
  'Region',
  'SavedStates',
  'State',
  'SystemScope',
  'User'
] as const

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      postApiAssessments: build.mutation<
        PostApiAssessmentsApiResponse,
        PostApiAssessmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments`,
          method: 'POST',
          body: queryArg.createAssessmentCommand
        }),
        invalidatesTags: ['Assessment']
      }),
      getApiAssessments: build.query<
        GetApiAssessmentsApiResponse,
        GetApiAssessmentsApiArg
      >({
        query: () => ({ url: `/assessments` }),
        providesTags: ['Assessment']
      }),
      patchApiAssessments: build.mutation<
        PatchApiAssessmentsApiResponse,
        PatchApiAssessmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments`,
          method: 'PATCH',
          body: queryArg.patchAssessmentCommand
        }),
        invalidatesTags: ['Assessment']
      }),
      getApiAssessmentsById: build.query<
        GetApiAssessmentsByIdApiResponse,
        GetApiAssessmentsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/assessments/${queryArg.id}` }),
        providesTags: ['Assessment']
      }),
      deleteApiAssessmentsById: build.mutation<
        DeleteApiAssessmentsByIdApiResponse,
        DeleteApiAssessmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Assessment']
      }),
      putApiAssessmentsById: build.mutation<
        PutApiAssessmentsByIdApiResponse,
        PutApiAssessmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateAssessmentCommand
        }),
        invalidatesTags: ['Assessment']
      }),
      deleteApiAssessmentsExcludeArcServerById: build.mutation<
        DeleteApiAssessmentsExcludeArcServerByIdApiResponse,
        DeleteApiAssessmentsExcludeArcServerByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments/excludeArcServer/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Assessment']
      }),
      postApiAssessmentsTerminateById: build.mutation<
        PostApiAssessmentsTerminateByIdApiResponse,
        PostApiAssessmentsTerminateByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments/terminate/${queryArg.id}`,
          method: 'POST'
        }),
        invalidatesTags: ['Assessment']
      }),
      postApiAssessmentsFilteredSegmentList: build.mutation<
        PostApiAssessmentsFilteredSegmentListApiResponse,
        PostApiAssessmentsFilteredSegmentListApiArg
      >({
        query: (queryArg) => ({
          url: `/assessments/FilteredSegmentList`,
          method: 'POST',
          body: queryArg.getAssessmentMapDataByIdQuery
        }),
        invalidatesTags: ['Assessment']
      }),
      postApiAssessmentScenarios: build.mutation<
        PostApiAssessmentScenariosApiResponse,
        PostApiAssessmentScenariosApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios`,
          method: 'POST',
          body: queryArg.createAssessmentScenarioCommand
        }),
        invalidatesTags: ['AssessmentScenario']
      }),
      getApiAssessmentScenarios: build.query<
        GetApiAssessmentScenariosApiResponse,
        GetApiAssessmentScenariosApiArg
      >({
        query: () => ({ url: `/assessmentScenarios` }),
        providesTags: ['AssessmentScenario']
      }),
      getApiAssessmentScenariosById: build.query<
        GetApiAssessmentScenariosByIdApiResponse,
        GetApiAssessmentScenariosByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios/${queryArg.id}`
        }),
        providesTags: ['AssessmentScenario']
      }),
      putApiAssessmentScenariosById: build.mutation<
        PutApiAssessmentScenariosByIdApiResponse,
        PutApiAssessmentScenariosByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateAssessmentScenarioCommand
        }),
        invalidatesTags: ['AssessmentScenario']
      }),
      getApiAssessmentScenariosScenarioDetail: build.query<
        GetApiAssessmentScenariosScenarioDetailApiResponse,
        GetApiAssessmentScenariosScenarioDetailApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios/scenarioDetail`,
          params: {
            id: queryArg.id,
            threatId: queryArg.threatId
          }
        }),
        providesTags: ['AssessmentScenario']
      }),
      getApiAssessmentScenariosScenario: build.query<
        GetApiAssessmentScenariosScenarioApiResponse,
        GetApiAssessmentScenariosScenarioApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios/scenario`,
          params: {
            threatId: queryArg.threatId
          }
        }),
        providesTags: ['AssessmentScenario']
      }),
      getApiAssessmentScenariosAttributeEnum: build.query<
        GetApiAssessmentScenariosAttributeEnumApiResponse,
        GetApiAssessmentScenariosAttributeEnumApiArg
      >({
        query: (queryArg) => ({
          url: `/assessmentScenarios/attributeEnum`,
          params: {
            attributeCode: queryArg.attributeCode
          }
        }),
        providesTags: ['AssessmentScenario']
      }),
      getApiBackgroundprocessesByExecutionIdStatus: build.query<
        GetApiBackgroundprocessesByExecutionIdStatusApiResponse,
        GetApiBackgroundprocessesByExecutionIdStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/backgroundprocesses/${queryArg.executionId}/status`
        }),
        providesTags: ['BackgroundProcesses']
      }),
      postApiBackgroundprocessesDataupdate: build.mutation<
        PostApiBackgroundprocessesDataupdateApiResponse,
        PostApiBackgroundprocessesDataupdateApiArg
      >({
        query: (queryArg) => ({
          url: `/backgroundprocesses/dataupdate`,
          method: 'POST',
          params: {
            isClientEnvironment: queryArg.isClientEnvironment,
            deleteCurrentBaselines: queryArg.deleteCurrentBaselines
          }
        }),
        invalidatesTags: ['BackgroundProcesses']
      }),
      postApiBackgroundprocessesPhmsaSegmentationUpdate: build.mutation<
        PostApiBackgroundprocessesPhmsaSegmentationUpdateApiResponse,
        PostApiBackgroundprocessesPhmsaSegmentationUpdateApiArg
      >({
        query: () => ({
          url: `/backgroundprocesses/phmsaSegmentationUpdate`,
          method: 'POST'
        }),
        invalidatesTags: ['BackgroundProcesses']
      }),
      postApiBackgroundprocessesLrsGeommetry: build.mutation<
        PostApiBackgroundprocessesLrsGeommetryApiResponse,
        PostApiBackgroundprocessesLrsGeommetryApiArg
      >({
        query: () => ({
          url: `/backgroundprocesses/LRSGeommetry`,
          method: 'POST'
        }),
        invalidatesTags: ['BackgroundProcesses']
      }),
      getApiBaseline: build.query<
        GetApiBaselineApiResponse,
        GetApiBaselineApiArg
      >({
        query: () => ({ url: `/baseline` }),
        providesTags: ['Baseline']
      }),
      getApiBaselineById: build.query<
        GetApiBaselineByIdApiResponse,
        GetApiBaselineByIdApiArg
      >({
        query: (queryArg) => ({ url: `/baseline/${queryArg.id}` }),
        providesTags: ['Baseline']
      }),
      getApiBaselineCurrent: build.query<
        GetApiBaselineCurrentApiResponse,
        GetApiBaselineCurrentApiArg
      >({
        query: () => ({ url: `/baseline/current` }),
        providesTags: ['Baseline']
      }),
      putApiBaselinePublishById: build.mutation<
        PutApiBaselinePublishByIdApiResponse,
        PutApiBaselinePublishByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/baseline/publish/${queryArg.id}`,
          method: 'PUT'
        }),
        invalidatesTags: ['Baseline']
      }),
      postApiProcessesRecalculatebaseline: build.mutation<
        PostApiProcessesRecalculatebaselineApiResponse,
        PostApiProcessesRecalculatebaselineApiArg
      >({
        query: () => ({
          url: `/processes/recalculatebaseline`,
          method: 'POST'
        }),
        invalidatesTags: ['BaselineRecalculation']
      }),
      getApiBasesettings: build.query<
        GetApiBasesettingsApiResponse,
        GetApiBasesettingsApiArg
      >({
        query: (queryArg) => ({
          url: `/basesettings`,
          params: {
            domain: queryArg.domain
          }
        }),
        providesTags: ['BaseSettings']
      }),
      postApiClasslocation: build.mutation<
        PostApiClasslocationApiResponse,
        PostApiClasslocationApiArg
      >({
        query: (queryArg) => ({
          url: `/classlocation`,
          method: 'POST',
          body: queryArg.hmcaClassLocationRequestParamDto,
          params: {
            systemScopeId: queryArg.systemScopeId
          }
        }),
        invalidatesTags: ['ClassLocation']
      }),
      postApiClasslocationExport: build.mutation<
        PostApiClasslocationExportApiResponse,
        PostApiClasslocationExportApiArg
      >({
        query: (queryArg) => ({
          url: `/classlocation/export`,
          method: 'POST',
          body: queryArg.hmcaClassLocationRequestParamDto,
          params: {
            systemScopeId: queryArg.systemScopeId
          }
        }),
        invalidatesTags: ['ClassLocation']
      }),
      getApiClasslocationAssessments: build.query<
        GetApiClasslocationAssessmentsApiResponse,
        GetApiClasslocationAssessmentsApiArg
      >({
        query: () => ({ url: `/classlocation/assessments` }),
        providesTags: ['ClassLocation']
      }),
      getApiClasslocationAssessmentsById: build.query<
        GetApiClasslocationAssessmentsByIdApiResponse,
        GetApiClasslocationAssessmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/classlocation/assessments/${queryArg.id}`
        }),
        providesTags: ['ClassLocation']
      }),
      getApiClasslocationAssessmentsCurrent: build.query<
        GetApiClasslocationAssessmentsCurrentApiResponse,
        GetApiClasslocationAssessmentsCurrentApiArg
      >({
        query: () => ({ url: `/classlocation/assessments/current` }),
        providesTags: ['ClassLocation']
      }),
      postApiColourbyvalue: build.mutation<
        PostApiColourbyvalueApiResponse,
        PostApiColourbyvalueApiArg
      >({
        query: (queryArg) => ({
          url: `/colourbyvalue`,
          method: 'POST',
          body: queryArg.createColourByValueCommand
        }),
        invalidatesTags: ['ColourByValue']
      }),
      putApiColourbyvalue: build.mutation<
        PutApiColourbyvalueApiResponse,
        PutApiColourbyvalueApiArg
      >({
        query: (queryArg) => ({
          url: `/colourbyvalue`,
          method: 'PUT',
          body: queryArg.updateColourByValueCommand
        }),
        invalidatesTags: ['ColourByValue']
      }),
      getApiColourbyvalueById: build.query<
        GetApiColourbyvalueByIdApiResponse,
        GetApiColourbyvalueByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/colourbyvalue/${queryArg.id}`,
          params: {
            featureName: queryArg.featureName
          }
        }),
        providesTags: ['ColourByValue']
      }),
      postApiConsequencearea: build.mutation<
        PostApiConsequenceareaApiResponse,
        PostApiConsequenceareaApiArg
      >({
        query: (queryArg) => ({
          url: `/consequencearea`,
          method: 'POST',
          body: queryArg.hmcaClassLocationRequestParamDto,
          params: {
            systemScopeId: queryArg.systemScopeId
          }
        }),
        invalidatesTags: ['ConsequenceArea']
      }),
      postApiConsequenceareaExport: build.mutation<
        PostApiConsequenceareaExportApiResponse,
        PostApiConsequenceareaExportApiArg
      >({
        query: (queryArg) => ({
          url: `/consequencearea/export`,
          method: 'POST',
          body: queryArg.hmcaClassLocationRequestParamDto,
          params: {
            systemScopeId: queryArg.systemScopeId
          }
        }),
        invalidatesTags: ['ConsequenceArea']
      }),
      getApiConsequenceareaAssessments: build.query<
        GetApiConsequenceareaAssessmentsApiResponse,
        GetApiConsequenceareaAssessmentsApiArg
      >({
        query: () => ({ url: `/consequencearea/assessments` }),
        providesTags: ['ConsequenceArea']
      }),
      getApiConsequenceareaAssessmentsById: build.query<
        GetApiConsequenceareaAssessmentsByIdApiResponse,
        GetApiConsequenceareaAssessmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/consequencearea/assessments/${queryArg.id}`
        }),
        providesTags: ['ConsequenceArea']
      }),
      getApiConsequenceareaAssessmentsCurrent: build.query<
        GetApiConsequenceareaAssessmentsCurrentApiResponse,
        GetApiConsequenceareaAssessmentsCurrentApiArg
      >({
        query: () => ({ url: `/consequencearea/assessments/current` }),
        providesTags: ['ConsequenceArea']
      }),
      getApiDatamanagementCustomviews: build.query<
        GetApiDatamanagementCustomviewsApiResponse,
        GetApiDatamanagementCustomviewsApiArg
      >({
        query: () => ({ url: `/datamanagement/customviews` }),
        providesTags: ['CustomView']
      }),
      postApiDatamanagementCustomviews: build.mutation<
        PostApiDatamanagementCustomviewsApiResponse,
        PostApiDatamanagementCustomviewsApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/customviews`,
          method: 'POST',
          body: queryArg.createCustomViewCommand
        }),
        invalidatesTags: ['CustomView']
      }),
      putApiDatamanagementCustomviewsById: build.mutation<
        PutApiDatamanagementCustomviewsByIdApiResponse,
        PutApiDatamanagementCustomviewsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/customviews/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateCustomViewCommand
        }),
        invalidatesTags: ['CustomView']
      }),
      getApiDatamanagementCustomviewsById: build.query<
        GetApiDatamanagementCustomviewsByIdApiResponse,
        GetApiDatamanagementCustomviewsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/customviews/${queryArg.id}`
        }),
        providesTags: ['CustomView']
      }),
      deleteApiDatamanagementCustomviewsById: build.mutation<
        DeleteApiDatamanagementCustomviewsByIdApiResponse,
        DeleteApiDatamanagementCustomviewsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/customviews/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['CustomView']
      }),
      getApiDatamanagementTableattributes: build.query<
        GetApiDatamanagementTableattributesApiResponse,
        GetApiDatamanagementTableattributesApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/tableattributes`,
          params: {
            param: queryArg.param
          }
        }),
        providesTags: ['DataManagement']
      }),
      postApiDatamanagementExportEventtable: build.mutation<
        PostApiDatamanagementExportEventtableApiResponse,
        PostApiDatamanagementExportEventtableApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/export/eventtable`,
          method: 'POST',
          body: queryArg.eventTableExportRequestParamDto
        }),
        invalidatesTags: ['DataManagement']
      }),
      postApiDatamanagementExportCodeguide: build.mutation<
        PostApiDatamanagementExportCodeguideApiResponse,
        PostApiDatamanagementExportCodeguideApiArg
      >({
        query: () => ({
          url: `/datamanagement/export/codeguide`,
          method: 'POST'
        }),
        invalidatesTags: ['DataManagement']
      }),
      postApiDatamanagementImportfiles: build.mutation<
        PostApiDatamanagementImportfilesApiResponse,
        PostApiDatamanagementImportfilesApiArg
      >({
        query: () => ({
          url: `/datamanagement/importfiles`,
          method: 'POST'
        }),
        invalidatesTags: ['DataManagement']
      }),
      postApiDatamanagementImportCommit: build.mutation<
        PostApiDatamanagementImportCommitApiResponse,
        PostApiDatamanagementImportCommitApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/import/commit`,
          method: 'POST',
          body: queryArg.eventTableCommitRequestParamDto
        }),
        invalidatesTags: ['DataManagement']
      }),
      getApiDatamanagementDownloadCsvfile: build.query<
        GetApiDatamanagementDownloadCsvfileApiResponse,
        GetApiDatamanagementDownloadCsvfileApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/download/csvfile`,
          params: {
            fileGroupId: queryArg.fileGroupId,
            fileName: queryArg.fileName
          }
        }),
        providesTags: ['DataManagement']
      }),
      getApiDatamanagementImports: build.query<
        GetApiDatamanagementImportsApiResponse,
        GetApiDatamanagementImportsApiArg
      >({
        query: () => ({ url: `/datamanagement/imports` }),
        providesTags: ['DataManagement']
      }),
      getApiDatamanagementImportdetailsById: build.query<
        GetApiDatamanagementImportdetailsByIdApiResponse,
        GetApiDatamanagementImportdetailsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/importdetails/${queryArg.id}`
        }),
        providesTags: ['DataManagement']
      }),
      postApiDatamanagementDataviewById: build.mutation<
        PostApiDatamanagementDataviewByIdApiResponse,
        PostApiDatamanagementDataviewByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/dataview/${queryArg.id}`,
          method: 'POST',
          body: queryArg.eventTableRequest
        }),
        invalidatesTags: ['DataManagement']
      }),
      getApiDatamanagementColumndefinitionById: build.query<
        GetApiDatamanagementColumndefinitionByIdApiResponse,
        GetApiDatamanagementColumndefinitionByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/datamanagement/columndefinition/${queryArg.id}`,
          params: {
            systemScopeId: queryArg.systemScopeId
          }
        }),
        providesTags: ['DataManagement']
      }),
      getApiDataupdateEventsAttributes: build.query<
        GetApiDataupdateEventsAttributesApiResponse,
        GetApiDataupdateEventsAttributesApiArg
      >({
        query: () => ({ url: `/dataupdate/events/attributes` }),
        providesTags: ['DataUpdate']
      }),
      getApiEsriEsriToken: build.query<
        GetApiEsriEsriTokenApiResponse,
        GetApiEsriEsriTokenApiArg
      >({
        query: () => ({ url: `/esri/esriToken` }),
        providesTags: ['Esri']
      }),
      getApiGassystems: build.query<
        GetApiGassystemsApiResponse,
        GetApiGassystemsApiArg
      >({
        query: () => ({ url: `/gassystems` }),
        providesTags: ['GasSystem']
      }),
      getApiGassystemsContinousRoute: build.query<
        GetApiGassystemsContinousRouteApiResponse,
        GetApiGassystemsContinousRouteApiArg
      >({
        query: () => ({ url: `/gassystems/continousRoute` }),
        providesTags: ['GasSystem']
      }),
      getApiGassystemsById: build.query<
        GetApiGassystemsByIdApiResponse,
        GetApiGassystemsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/gassystems/${queryArg.id}` }),
        providesTags: ['GasSystem']
      }),
      getApiGassystemsContinuousrouteById: build.query<
        GetApiGassystemsContinuousrouteByIdApiResponse,
        GetApiGassystemsContinuousrouteByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/gassystems/continuousroute/${queryArg.id}`
        }),
        providesTags: ['GasSystem']
      }),
      postApiGassystemsContinuousroute: build.mutation<
        PostApiGassystemsContinuousrouteApiResponse,
        PostApiGassystemsContinuousrouteApiArg
      >({
        query: (queryArg) => ({
          url: `/gassystems/continuousroute`,
          method: 'POST',
          body: queryArg.getSearchProfileGasSystemContinuousRouteQuery
        }),
        invalidatesTags: ['GasSystem']
      }),
      getApiGroupTypes: build.query<
        GetApiGroupTypesApiResponse,
        GetApiGroupTypesApiArg
      >({
        query: () => ({ url: `/groupTypes` }),
        providesTags: ['GroupType']
      }),
      getApiJobsById: build.query<
        GetApiJobsByIdApiResponse,
        GetApiJobsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/jobs/${queryArg.id}` }),
        providesTags: ['Job']
      }),
      getApiJobsByIdStatus: build.query<
        GetApiJobsByIdStatusApiResponse,
        GetApiJobsByIdStatusApiArg
      >({
        query: (queryArg) => ({ url: `/jobs/${queryArg.id}/status` }),
        providesTags: ['Job']
      }),
      patchApiJobsByIdStatus: build.mutation<
        PatchApiJobsByIdStatusApiResponse,
        PatchApiJobsByIdStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/jobs/${queryArg.id}/status`,
          method: 'PATCH',
          body: queryArg.body
        }),
        invalidatesTags: ['Job']
      }),
      getApiOperators: build.query<
        GetApiOperatorsApiResponse,
        GetApiOperatorsApiArg
      >({
        query: (queryArg) => ({
          url: `/operators`,
          params: {
            regionCode: queryArg.regionCode,
            stateCode: queryArg.stateCode
          }
        }),
        providesTags: ['Operator']
      }),
      getApiOperatorsById: build.query<
        GetApiOperatorsByIdApiResponse,
        GetApiOperatorsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/operators/${queryArg.id}` }),
        providesTags: ['Operator']
      }),
      postApiPhmsaExport: build.mutation<
        PostApiPhmsaExportApiResponse,
        PostApiPhmsaExportApiArg
      >({
        query: (queryArg) => ({
          url: `/phmsa/export`,
          method: 'POST',
          body: queryArg.phmsaExportRequestParamDto
        }),
        invalidatesTags: ['PhmsaExport']
      }),
      postApiQraDatatable: build.mutation<
        PostApiQraDatatableApiResponse,
        PostApiQraDatatableApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/datatable`,
          method: 'POST',
          body: queryArg.tableRequestParamsDto,
          params: {
            systemScopeId: queryArg.systemScopeId,
            continuousRouteId: queryArg.continuousRouteId,
            savedStateId1: queryArg.savedStateId1,
            savedStateId2: queryArg.savedStateId2,
            assessmentId: queryArg.assessmentId,
            offsetRecord: queryArg.offsetRecord,
            totalRecords: queryArg.totalRecords
          }
        }),
        invalidatesTags: ['QraDataTable']
      }),
      postApiQraDatatableExport: build.mutation<
        PostApiQraDatatableExportApiResponse,
        PostApiQraDatatableExportApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/datatable/export`,
          method: 'POST',
          body: queryArg.tableRequestParamsDto,
          params: {
            systemScopeId: queryArg.systemScopeId,
            continuousRouteId: queryArg.continuousRouteId,
            savedStateId1: queryArg.savedStateId1,
            savedStateId2: queryArg.savedStateId2,
            assessmentId: queryArg.assessmentId,
            offsetRecord: queryArg.offsetRecord,
            totalRecords: queryArg.totalRecords
          }
        }),
        invalidatesTags: ['QraDataTable']
      }),
      postApiQraDrilldown: build.mutation<
        PostApiQraDrilldownApiResponse,
        PostApiQraDrilldownApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/drilldown`,
          method: 'POST',
          body: queryArg.agGridRequest,
          params: {
            assessmentId1: queryArg.assessmentId1,
            assessmentId2: queryArg.assessmentId2
          }
        }),
        invalidatesTags: ['QraDataTableDrillDown']
      }),
      postApiQraGraphs: build.mutation<
        PostApiQraGraphsApiResponse,
        PostApiQraGraphsApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/graphs`,
          method: 'POST',
          body: queryArg.graphRequestParamsDto,
          params: {
            systemScopeId: queryArg.systemScopeId,
            continuousRouteId: queryArg.continuousRouteId,
            savedStateId1: queryArg.savedStateId1,
            savedStateId2: queryArg.savedStateId2,
            assessmentId: queryArg.assessmentId
          }
        }),
        invalidatesTags: ['QraGraph']
      }),
      getApiRegions: build.query<GetApiRegionsApiResponse, GetApiRegionsApiArg>(
        {
          query: (queryArg) => ({
            url: `/regions`,
            params: {
              operatorCode: queryArg.operatorCode,
              stateCode: queryArg.stateCode
            }
          }),
          providesTags: ['Region']
        }
      ),
      postApiQraSavedstates: build.mutation<
        PostApiQraSavedstatesApiResponse,
        PostApiQraSavedstatesApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/savedstates`,
          method: 'POST',
          body: queryArg.createSavedStateCommand
        }),
        invalidatesTags: ['SavedStates']
      }),
      getApiQraSavedstates: build.query<
        GetApiQraSavedstatesApiResponse,
        GetApiQraSavedstatesApiArg
      >({
        query: () => ({ url: `/qra/savedstates` }),
        providesTags: ['SavedStates']
      }),
      getApiQraSavedstatesById: build.query<
        GetApiQraSavedstatesByIdApiResponse,
        GetApiQraSavedstatesByIdApiArg
      >({
        query: (queryArg) => ({ url: `/qra/savedstates/${queryArg.id}` }),
        providesTags: ['SavedStates']
      }),
      putApiQraSavedstatesById: build.mutation<
        PutApiQraSavedstatesByIdApiResponse,
        PutApiQraSavedstatesByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/qra/savedstates/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateSavedStateCommand
        }),
        invalidatesTags: ['SavedStates']
      }),
      getApiStates: build.query<GetApiStatesApiResponse, GetApiStatesApiArg>({
        query: (queryArg) => ({
          url: `/states`,
          params: {
            operatorCode: queryArg.operatorCode,
            regionCode: queryArg.regionCode
          }
        }),
        providesTags: ['State']
      }),
      getApiSystemscopes: build.query<
        GetApiSystemscopesApiResponse,
        GetApiSystemscopesApiArg
      >({
        query: () => ({ url: `/systemscopes` }),
        providesTags: ['SystemScope']
      }),
      postApiSystemscopes: build.mutation<
        PostApiSystemscopesApiResponse,
        PostApiSystemscopesApiArg
      >({
        query: (queryArg) => ({
          url: `/systemscopes`,
          method: 'POST',
          body: queryArg.createSystemScopeCommand
        }),
        invalidatesTags: ['SystemScope']
      }),
      putApiSystemscopes: build.mutation<
        PutApiSystemscopesApiResponse,
        PutApiSystemscopesApiArg
      >({
        query: (queryArg) => ({
          url: `/systemscopes`,
          method: 'PUT',
          body: queryArg.updateSystemScopeCommand
        }),
        invalidatesTags: ['SystemScope']
      }),
      getApiSystemscopesActive: build.query<
        GetApiSystemscopesActiveApiResponse,
        GetApiSystemscopesActiveApiArg
      >({
        query: () => ({ url: `/systemscopes/active` }),
        providesTags: ['SystemScope']
      }),
      putApiSystemscopesActive: build.mutation<
        PutApiSystemscopesActiveApiResponse,
        PutApiSystemscopesActiveApiArg
      >({
        query: (queryArg) => ({
          url: `/systemscopes/active`,
          method: 'PUT',
          body: queryArg.updateSystemScopeUserCommand
        }),
        invalidatesTags: ['SystemScope']
      }),
      getApiSystemscopesById: build.query<
        GetApiSystemscopesByIdApiResponse,
        GetApiSystemscopesByIdApiArg
      >({
        query: (queryArg) => ({ url: `/systemscopes/${queryArg.id}` }),
        providesTags: ['SystemScope']
      }),
      deleteApiSystemscopesById: build.mutation<
        DeleteApiSystemscopesByIdApiResponse,
        DeleteApiSystemscopesByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/systemscopes/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['SystemScope']
      }),
      getApiUsersGetCurrentUserId: build.query<
        GetApiUsersGetCurrentUserIdApiResponse,
        GetApiUsersGetCurrentUserIdApiArg
      >({
        query: () => ({ url: `/users/getCurrentUserId` }),
        providesTags: ['User']
      })
    }),
    overrideExisting: false
  })

export { injectedRtkApi as generatedApi }

export type PostApiAssessmentsApiResponse = unknown

export type PostApiAssessmentsApiArg = {
  createAssessmentCommand: CreateAssessmentCommand
}

export type GetApiAssessmentsApiResponse = unknown

export type GetApiAssessmentsApiArg = void

export type PatchApiAssessmentsApiResponse = unknown

export type PatchApiAssessmentsApiArg = {
  patchAssessmentCommand: PatchAssessmentCommand
}

export type GetApiAssessmentsByIdApiResponse = unknown

export type GetApiAssessmentsByIdApiArg = {
  id: number
}

export type DeleteApiAssessmentsByIdApiResponse = unknown

export type DeleteApiAssessmentsByIdApiArg = {
  id: number
}

export type PutApiAssessmentsByIdApiResponse = unknown

export type PutApiAssessmentsByIdApiArg = {
  id: number
  updateAssessmentCommand: UpdateAssessmentCommand
}

export type DeleteApiAssessmentsExcludeArcServerByIdApiResponse = unknown

export type DeleteApiAssessmentsExcludeArcServerByIdApiArg = {
  id: number
}

export type PostApiAssessmentsTerminateByIdApiResponse = unknown

export type PostApiAssessmentsTerminateByIdApiArg = {
  id: number
}

export type PostApiAssessmentsFilteredSegmentListApiResponse = unknown

export type PostApiAssessmentsFilteredSegmentListApiArg = {
  getAssessmentMapDataByIdQuery: GetAssessmentMapDataByIdQuery
}

export type PostApiAssessmentScenariosApiResponse = unknown

export type PostApiAssessmentScenariosApiArg = {
  createAssessmentScenarioCommand: CreateAssessmentScenarioCommand
}

export type GetApiAssessmentScenariosApiResponse = unknown

export type GetApiAssessmentScenariosApiArg = void

export type GetApiAssessmentScenariosByIdApiResponse = unknown

export type GetApiAssessmentScenariosByIdApiArg = {
  id: number
}

export type PutApiAssessmentScenariosByIdApiResponse = unknown

export type PutApiAssessmentScenariosByIdApiArg = {
  id: number
  updateAssessmentScenarioCommand: UpdateAssessmentScenarioCommand
}

export type GetApiAssessmentScenariosScenarioDetailApiResponse = unknown

export type GetApiAssessmentScenariosScenarioDetailApiArg = {
  id?: number
  threatId?: number
}

export type GetApiAssessmentScenariosScenarioApiResponse = unknown

export type GetApiAssessmentScenariosScenarioApiArg = {
  threatId?: number
}

export type GetApiAssessmentScenariosAttributeEnumApiResponse = unknown

export type GetApiAssessmentScenariosAttributeEnumApiArg = {
  attributeCode?: string
}

export type GetApiBackgroundprocessesByExecutionIdStatusApiResponse = unknown

export type GetApiBackgroundprocessesByExecutionIdStatusApiArg = {
  executionId: string
}

export type PostApiBackgroundprocessesDataupdateApiResponse = unknown

export type PostApiBackgroundprocessesDataupdateApiArg = {
  isClientEnvironment?: boolean
  deleteCurrentBaselines?: boolean
}

export type PostApiBackgroundprocessesPhmsaSegmentationUpdateApiResponse =
  unknown

export type PostApiBackgroundprocessesPhmsaSegmentationUpdateApiArg = void

export type PostApiBackgroundprocessesLrsGeommetryApiResponse = unknown

export type PostApiBackgroundprocessesLrsGeommetryApiArg = void

export type GetApiBaselineApiResponse = unknown

export type GetApiBaselineApiArg = void

export type GetApiBaselineByIdApiResponse = unknown

export type GetApiBaselineByIdApiArg = {
  id: number
}

export type GetApiBaselineCurrentApiResponse = unknown

export type GetApiBaselineCurrentApiArg = void

export type PutApiBaselinePublishByIdApiResponse = unknown

export type PutApiBaselinePublishByIdApiArg = {
  id: number
}

export type PostApiProcessesRecalculatebaselineApiResponse = unknown

export type PostApiProcessesRecalculatebaselineApiArg = void

export type GetApiBasesettingsApiResponse = unknown

export type GetApiBasesettingsApiArg = {
  domain?: string
}

export type PostApiClasslocationApiResponse = unknown

export type PostApiClasslocationApiArg = {
  systemScopeId?: number
  hmcaClassLocationRequestParamDto: HmcaClassLocationRequestParamDto
}

export type PostApiClasslocationExportApiResponse = unknown

export type PostApiClasslocationExportApiArg = {
  systemScopeId?: number
  hmcaClassLocationRequestParamDto: HmcaClassLocationRequestParamDto
}

export type GetApiClasslocationAssessmentsApiResponse = unknown

export type GetApiClasslocationAssessmentsApiArg = void

export type GetApiClasslocationAssessmentsByIdApiResponse = unknown

export type GetApiClasslocationAssessmentsByIdApiArg = {
  id: number
}

export type GetApiClasslocationAssessmentsCurrentApiResponse = unknown

export type GetApiClasslocationAssessmentsCurrentApiArg = void

export type PostApiColourbyvalueApiResponse = unknown

export type PostApiColourbyvalueApiArg = {
  createColourByValueCommand: CreateColourByValueCommand
}

export type PutApiColourbyvalueApiResponse = unknown

export type PutApiColourbyvalueApiArg = {
  updateColourByValueCommand: UpdateColourByValueCommand
}

export type GetApiColourbyvalueByIdApiResponse = unknown

export type GetApiColourbyvalueByIdApiArg = {
  id: number
  featureName?: string
}

export type PostApiConsequenceareaApiResponse = unknown

export type PostApiConsequenceareaApiArg = {
  systemScopeId?: number
  hmcaClassLocationRequestParamDto: HmcaClassLocationRequestParamDto
}

export type PostApiConsequenceareaExportApiResponse = unknown

export type PostApiConsequenceareaExportApiArg = {
  systemScopeId?: number
  hmcaClassLocationRequestParamDto: HmcaClassLocationRequestParamDto
}

export type GetApiConsequenceareaAssessmentsApiResponse = unknown

export type GetApiConsequenceareaAssessmentsApiArg = void

export type GetApiConsequenceareaAssessmentsByIdApiResponse = unknown

export type GetApiConsequenceareaAssessmentsByIdApiArg = {
  id: number
}

export type GetApiConsequenceareaAssessmentsCurrentApiResponse = unknown

export type GetApiConsequenceareaAssessmentsCurrentApiArg = void

export type GetApiDatamanagementCustomviewsApiResponse = unknown

export type GetApiDatamanagementCustomviewsApiArg = void

export type PostApiDatamanagementCustomviewsApiResponse = unknown

export type PostApiDatamanagementCustomviewsApiArg = {
  createCustomViewCommand: CreateCustomViewCommand
}

export type PutApiDatamanagementCustomviewsByIdApiResponse = unknown

export type PutApiDatamanagementCustomviewsByIdApiArg = {
  id: number
  updateCustomViewCommand: UpdateCustomViewCommand
}

export type GetApiDatamanagementCustomviewsByIdApiResponse = unknown

export type GetApiDatamanagementCustomviewsByIdApiArg = {
  id: number
}

export type DeleteApiDatamanagementCustomviewsByIdApiResponse = unknown

export type DeleteApiDatamanagementCustomviewsByIdApiArg = {
  id: number
}

export type GetApiDatamanagementTableattributesApiResponse = unknown

export type GetApiDatamanagementTableattributesApiArg = {
  param?: string
}

export type PostApiDatamanagementExportEventtableApiResponse = unknown

export type PostApiDatamanagementExportEventtableApiArg = {
  eventTableExportRequestParamDto: EventTableExportRequestParamDto
}

export type PostApiDatamanagementExportCodeguideApiResponse = unknown

export type PostApiDatamanagementExportCodeguideApiArg = void

export type PostApiDatamanagementImportfilesApiResponse =
  /** status 200 Success */ any

export type PostApiDatamanagementImportfilesApiArg = void

export type PostApiDatamanagementImportCommitApiResponse = unknown

export type PostApiDatamanagementImportCommitApiArg = {
  eventTableCommitRequestParamDto: EventTableCommitRequestParamDto
}

export type GetApiDatamanagementDownloadCsvfileApiResponse = unknown

export type GetApiDatamanagementDownloadCsvfileApiArg = {
  fileGroupId?: string
  fileName?: string
}

export type GetApiDatamanagementImportsApiResponse = unknown

export type GetApiDatamanagementImportsApiArg = void

export type GetApiDatamanagementImportdetailsByIdApiResponse = unknown

export type GetApiDatamanagementImportdetailsByIdApiArg = {
  id: number
}

export type PostApiDatamanagementDataviewByIdApiResponse = unknown

export type PostApiDatamanagementDataviewByIdApiArg = {
  id: number
  eventTableRequest: EventTableRequest
}

export type GetApiDatamanagementColumndefinitionByIdApiResponse = unknown

export type GetApiDatamanagementColumndefinitionByIdApiArg = {
  id: number
  systemScopeId?: number
}

export type GetApiDataupdateEventsAttributesApiResponse = unknown

export type GetApiDataupdateEventsAttributesApiArg = void

export type GetApiEsriEsriTokenApiResponse = unknown

export type GetApiEsriEsriTokenApiArg = void

export type GetApiGassystemsApiResponse = unknown

export type GetApiGassystemsApiArg = void

export type GetApiGassystemsContinousRouteApiResponse = unknown

export type GetApiGassystemsContinousRouteApiArg = void

export type GetApiGassystemsByIdApiResponse = unknown

export type GetApiGassystemsByIdApiArg = {
  id: number
}

export type GetApiGassystemsContinuousrouteByIdApiResponse = unknown

export type GetApiGassystemsContinuousrouteByIdApiArg = {
  id: number
}

export type PostApiGassystemsContinuousrouteApiResponse = unknown

export type PostApiGassystemsContinuousrouteApiArg = {
  getSearchProfileGasSystemContinuousRouteQuery: GetSearchProfileGasSystemContinuousRouteQuery
}

export type GetApiGroupTypesApiResponse = unknown

export type GetApiGroupTypesApiArg = void

export type GetApiJobsByIdApiResponse = unknown

export type GetApiJobsByIdApiArg = {
  id: string
}

export type GetApiJobsByIdStatusApiResponse = unknown

export type GetApiJobsByIdStatusApiArg = {
  id: string
}

export type PatchApiJobsByIdStatusApiResponse = unknown

export type PatchApiJobsByIdStatusApiArg = {
  id: string
  body: string
}

export type GetApiOperatorsApiResponse = unknown

export type GetApiOperatorsApiArg = {
  regionCode?: string[]
  stateCode?: string[]
}

export type GetApiOperatorsByIdApiResponse = unknown

export type GetApiOperatorsByIdApiArg = {
  id: number
}

export type PostApiPhmsaExportApiResponse = unknown

export type PostApiPhmsaExportApiArg = {
  phmsaExportRequestParamDto: PhmsaExportRequestParamDto
}

export type PostApiQraDatatableApiResponse = unknown

export type PostApiQraDatatableApiArg = {
  systemScopeId?: number
  continuousRouteId?: number
  savedStateId1?: number
  savedStateId2?: number
  assessmentId?: number
  offsetRecord?: number
  totalRecords?: number
  tableRequestParamsDto: TableRequestParamsDto
}

export type PostApiQraDatatableExportApiResponse = unknown

export type PostApiQraDatatableExportApiArg = {
  systemScopeId?: number
  continuousRouteId?: number
  savedStateId1?: number
  savedStateId2?: number
  assessmentId?: number
  offsetRecord?: number
  totalRecords?: number
  tableRequestParamsDto: TableRequestParamsDto
}

export type PostApiQraDrilldownApiResponse = unknown

export type PostApiQraDrilldownApiArg = {
  assessmentId1?: number
  assessmentId2?: number
  agGridRequest: AgGridRequest
}

export type PostApiQraGraphsApiResponse = unknown

export type PostApiQraGraphsApiArg = {
  systemScopeId?: number
  continuousRouteId?: number
  savedStateId1?: number
  savedStateId2?: number
  assessmentId?: number
  graphRequestParamsDto: GraphRequestParamsDto
}

export type GetApiRegionsApiResponse = unknown

export type GetApiRegionsApiArg = {
  operatorCode?: string[]
  stateCode?: string[]
}

export type PostApiQraSavedstatesApiResponse = unknown

export type PostApiQraSavedstatesApiArg = {
  createSavedStateCommand: CreateSavedStateCommand
}

export type GetApiQraSavedstatesApiResponse = unknown

export type GetApiQraSavedstatesApiArg = void

export type GetApiQraSavedstatesByIdApiResponse = unknown

export type GetApiQraSavedstatesByIdApiArg = {
  id: number
}

export type PutApiQraSavedstatesByIdApiResponse = unknown

export type PutApiQraSavedstatesByIdApiArg = {
  id: number
  updateSavedStateCommand: UpdateSavedStateCommand
}

export type GetApiStatesApiResponse = unknown

export type GetApiStatesApiArg = {
  operatorCode?: string[]
  regionCode?: string[]
}

export type GetApiSystemscopesApiResponse = unknown

export type GetApiSystemscopesApiArg = void

export type PostApiSystemscopesApiResponse = unknown

export type PostApiSystemscopesApiArg = {
  createSystemScopeCommand: CreateSystemScopeCommand
}

export type PutApiSystemscopesApiResponse = unknown

export type PutApiSystemscopesApiArg = {
  updateSystemScopeCommand: UpdateSystemScopeCommand
}

export type GetApiSystemscopesActiveApiResponse = unknown

export type GetApiSystemscopesActiveApiArg = void

export type PutApiSystemscopesActiveApiResponse = unknown

export type PutApiSystemscopesActiveApiArg = {
  updateSystemScopeUserCommand: UpdateSystemScopeUserCommand
}

export type GetApiSystemscopesByIdApiResponse = unknown

export type GetApiSystemscopesByIdApiArg = {
  id: number
}

export type DeleteApiSystemscopesByIdApiResponse = unknown

export type DeleteApiSystemscopesByIdApiArg = {
  id: number
}

export type GetApiUsersGetCurrentUserIdApiResponse = unknown

export type GetApiUsersGetCurrentUserIdApiArg = void

export type AssessmentContinuousRouteDto = {
  id?: number
  name?: string | null
  assessmentId?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
}

export type AssessmentRiskLevelDto = {
  id?: number
  name?: string | null
  assessmentId?: number
  code?: string | null
}

export type AssessmentClassLocationDto = {
  id?: number
  name?: string | null
  assessmentId?: number
  code?: string | null
}

export type ScenarioRuleCondition = {
  beforeCondition?: string | null
  beforeValue?: string | null
  afterCondition?: string | null
  afterValue?: string | null
}

export type AssessmentScenarioRuleDto = {
  attributeId?: string | null
  attributeCode?: string | null
  attributeName?: string | null
  attributeType?: string | null
  conditions?: ScenarioRuleCondition[] | null
}

export type AssessmentScenarioDto = {
  id?: number
  name?: string | null
  rules?: AssessmentScenarioRuleDto[] | null
  assessmentId?: number
}

export type AssessmentConsequenceLevelDto = {
  id?: number
  name?: string | null
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
}

export type AssessmentRiskModelDto = {
  id?: number
  assessmentId?: number
  riskModelId?: number
}

export type CreateAssessmentCommand = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  typeCode?: string | null
  statusCode?: string | null
  savedStateId?: number | null
  statusDescription?: string | null
  isPublished?: boolean
  remainingTimeEstimation?: string | null
  customQuery?: string | null
  customQueryJson?: string | null
  createdBy?: string
  modifiedBy?: string
  assessmentContinuousRoutes?: AssessmentContinuousRouteDto[] | null
  assessmentRiskLevels?: AssessmentRiskLevelDto[] | null
  assessmentClassLocations?: AssessmentClassLocationDto[] | null
  assessmentScenarios?: AssessmentScenarioDto[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevelDto[] | null
  assessmentRiskModels?: AssessmentRiskModelDto[] | null
}

export type PatchAssessmentCommand = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  typeCode?: string | null
  statusCode?: string | null
  savedStateId?: number | null
  statusDescription?: string | null
  isPublished?: boolean
  remainingTimeEstimation?: string | null
  customQuery?: string | null
  customQueryJson?: string | null
  createdBy?: string
  modifiedBy?: string
  assessmentContinuousRoutes?: AssessmentContinuousRouteDto[] | null
  assessmentRiskLevels?: AssessmentRiskLevelDto[] | null
  assessmentClassLocations?: AssessmentClassLocationDto[] | null
  assessmentScenarios?: AssessmentScenarioDto[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevelDto[] | null
  assessmentRiskModels?: AssessmentRiskModelDto[] | null
}

export type UpdateAssessmentCommand = {
  idUser?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  typeCode?: string | null
  statusCode?: string | null
  statusDescription?: string | null
  savedStateId?: number
  isPublished?: boolean
  remainingTimeEstimation?: string | null
  customQuery?: string | null
  customQueryJson?: string | null
  createdBy?: string
  modifiedBy?: string
  assessmentContinuousRoutes?: AssessmentContinuousRouteDto[] | null
  assessmentRiskLevels?: AssessmentRiskLevelDto[] | null
  assessmentClassLocations?: AssessmentClassLocationDto[] | null
  assessmentScenarios?: AssessmentScenarioDto[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevelDto[] | null
}

export type SegmentDto = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
}

export type HmcatypeDto = {
  id?: number
  code?: string | null
  name?: string | null
}

export type ClassLocationDto = {
  id?: number
  code?: string | null
  name?: string | null
}

export type RiskLevelDto = {
  id?: number
  name?: string | null
  code?: string | null
}

export type LocalFilterDto = {
  id?: number
  name?: string | null
  segments?: SegmentDto[] | null
  hmca?: HmcatypeDto[] | null
  classLocations?: ClassLocationDto[] | null
  riskLevels?: RiskLevelDto[] | null
  customQuery?: string | null
  customQueryString?: string | null
}

export type RiskValueRequestTypeDto = {
  valueType?: string | null
  unit?: string | null
}

export type AssessmentMapDataRequestParamsDto = {
  localFilter?: LocalFilterDto
  riskValue?: RiskValueRequestTypeDto
}

export type GetAssessmentMapDataByIdQuery = {
  idUser?: string
  esriToken?: string | null
  assessmentId?: number
  baselineId?: number
  requestParams?: AssessmentMapDataRequestParamsDto
}

export type CreateAssessmentScenarioCommand = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  rules?: AssessmentScenarioRuleDto[] | null
  createdBy?: string
  modifiedBy?: string
  userId?: string
  isPredefined?: boolean
}

export type UpdateAssessmentScenarioCommand = {
  idUser?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  rules?: AssessmentScenarioRuleDto[] | null
  createdBy?: string
  modifiedBy?: string
  userId?: string
  isPredefined?: boolean
}

export type HmcaClassLocationRequestParamDto = {
  fromDate?: string | null
  toDate?: string | null
  routeIds?: number[] | null
  classification?: string[] | null
}

export type CreateColourByValueCommand = {
  idUser?: string
  esriToken?: string | null
  tableId?: number
  feature?: string | null
  configuration?: string | null
}

export type UpdateColourByValueCommand = {
  idUser?: string
  esriToken?: string | null
  id?: number
  tableId?: number
  feature?: string | null
  configuration?: string | null
}

export type DataManagementCustomViewDetailDto = {
  id?: number
  eventTableAttributeId?: number
  attributeCustomAlias?: string | null
  dataManagementCustomViewId?: number
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  isSorted?: boolean | null
  isPinned?: boolean
  isHidden?: boolean
}

export type CreateCustomViewCommand = {
  idUser?: string
  esriToken?: string | null
  dataViewName?: string | null
  dataViewDescription?: string | null
  isCurrentSystemData?: boolean
  dataViewFromDate?: string
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  customViewDetailsDtoList?: DataManagementCustomViewDetailDto[] | null
}

export type UpdateCustomViewCommand = {
  idUser?: string
  esriToken?: string | null
  dataViewName?: string | null
  dataViewDescription?: string | null
  isCurrentSystemData?: boolean
  dataViewFromDate?: string
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  customViewDetailsDtoList?: DataManagementCustomViewDetailDto[] | null
}

export type EventTableAttributeEnumDto = {
  id?: number
  code?: string | null
  name?: string | null
}

export type EventTableAttributeDto = {
  id?: number
  name?: string | null
  code?: string | null
  dataType?: string | null
  eventTableId?: number
  eventTypeTable?: string | null
  minValue?: number | null
  maxValue?: number | null
  minDate?: string | null
  maxDate?: string | null
  attributeEnums?: EventTableAttributeEnumDto[] | null
  isSorted?: boolean | null
  isPinned?: boolean
  isHidden?: boolean
  dataManagementCustomViewDetails?: DataManagementCustomViewDetailDto[] | null
}

export type DataManagementCategoryDto = {
  id?: number
  categoryName?: string | null
}

export type EventTableDto = {
  id?: number
  name?: string | null
  entity?: string | null
  arcGisUrl?: string | null
  typeCode?: string | null
  eventTableAttributes?: EventTableAttributeDto[] | null
  dataManagementCategory?: DataManagementCategoryDto
}

export type EventTableExportRequestParamDto = {
  eventTableList?: EventTableDto[] | null
  exportDate?: string
  exportOption?: string | null
}

export type EventTableCommitRequestParamDto = {
  csvFileGroupId?: number
  commitAction?: string | null
}

export type SortModel = {
  colId?: string | null
  sort?: string | null
}

export type RowGroupCol = {
  id?: string | null
  aggFunc?: string | null
  displayName?: string | null
  field?: string | null
}

export type ValueCol = {
  id?: string | null
  aggFunc?: string | null
  displayName?: string | null
  field?: string | null
}

export type EventTableRequest = {
  startRow?: number
  endRow?: number
  sortModel?: SortModel[] | null
  rowGroupCols?: RowGroupCol[] | null
  valueCols?: ValueCol[] | null
  pivotCols?: any[] | null
  pivotMode?: boolean
  groupKeys?: any[] | null
  innerQuery?: string | null
  systemScopeId?: number
  sourceType?: string | null
  filterModel?: any
}

export type GetSearchProfileGasSystemContinuousRouteQuery = {
  customQueryString?: string | null
  operatorCodes?: string[] | null
  stateCodes?: string[] | null
  regionCodes?: string[] | null
}

export type PhmsaExportRequestParamDto = {
  operatorList?: string[] | null
  exportDate?: string
}

export type QraLineViewTableType = 0 | 1 | 2

export type TableRequestParamsDto = {
  tableType?: string[] | null
  localFilter?: LocalFilterDto
  riskValue?: RiskValueRequestTypeDto
  lineViewTableType?: QraLineViewTableType
}

export type JsonNodeOptions = {
  propertyNameCaseInsensitive?: boolean
}

export type JsonNode = {
  options?: JsonNodeOptions
  parent?: JsonNode
  root?: JsonNode
}

export type AgGridRequest = {
  startRow?: number
  endRow?: number
  sortModel?: SortModel[] | null
  rowGroupCols?: RowGroupCol[] | null
  valueCols?: ValueCol[] | null
  pivotCols?: any[] | null
  pivotMode?: boolean
  groupKeys?: any[] | null
  filterModel?: {
    [key: string]: JsonNode
  } | null
}

export type GraphSelectionDto = {
  riskByThreat?: boolean
  riskByConsequence?: boolean
  riskByLine?: boolean
  riskBySignificanceLevel?: boolean
  riskByOutcomeScenario?: boolean
  riskByReleaseType?: boolean
  riskBySystem?: boolean
  riskByConsequenceAreas?: boolean
  riskForecast?: boolean
  riskByLinearReference?: boolean
}

export type GraphRequestParamsDto = {
  graphSelection?: GraphSelectionDto
  riskValue?: RiskValueRequestTypeDto
  localFilter?: LocalFilterDto
}

export type AcceptanceCategoryDto = {
  id?: number
  minValue?: number
  maxValue?: number
  code?: string | null
  name?: string | null
  colour?: string | null
}

export type StringObjectKeyValuePair = {
  key?: string | null
  value?: any | null
}

export type GraphItem = {
  id?: number
  name?: string | null
  values?: StringObjectKeyValuePair[] | null
}

export type GraphDto = {
  graphType?: string | null
  acceptanceCategories?: AcceptanceCategoryDto[] | null
  items?: GraphItem[] | null
}

export type AssessmentDto = {
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  dateCreated?: string
  typeCode?: string | null
  statusCode?: string | null
  statusDescription?: string | null
  savedStateId?: number
  isPublished?: boolean
  remainingMinutesEstimation?: number
  customQuery?: string | null
  customQueryJson?: string | null
  totalSegmentCount?: number
  counterState?: number
  createdBy?: string
  modifiedBy?: string
  author?: string | null
  orchestratorResponseUrl?: string | null
  assessmentContinuousRoutes?: AssessmentContinuousRouteDto[] | null
  assessmentRiskLevels?: AssessmentRiskLevelDto[] | null
  assessmentClassLocations?: AssessmentClassLocationDto[] | null
  assessmentScenarios?: AssessmentScenarioDto[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevelDto[] | null
  assessmentRiskModels?: AssessmentRiskModelDto[] | null
}

export type SavedStateContinuousRouteDto = {
  id?: number
  savedStateId?: number
  continuousRouteId?: number
}

export type CreateSavedStateCommand = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  modelId?: number
  baselineId?: number
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  author?: string | null
  risks?: GraphDto
  assessments?: AssessmentDto[] | null
  savedStateContinuousRoutes?: SavedStateContinuousRouteDto[] | null
}

export type UpdateSavedStateCommand = {
  idUser?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  modelId?: number
  baselineId?: number
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  author?: string | null
  risks?: GraphDto
  assessments?: AssessmentDto[] | null
  savedStateContinuousRoutes?: SavedStateContinuousRouteDto[] | null
}

export type AssessmentClassLocation = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
}

export type AssessmentClassLocationRead = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
}

export type AssessmentRiskModel = {
  id?: number
  assessmentId?: number
  riskModelId?: number
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  riskModel?: RiskModel
}

export type AssessmentRiskModelRead = {
  id?: number
  assessmentId?: number
  riskModelId?: number
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  riskModel?: RiskModel
}

export type RiskResultWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  threatId?: number
  frequencyDensityPerLengthUnitYear?: number
  failureScenarioId?: number
  impactId?: number
  consequenceValue?: number
  riskDensityRUPerLengthYear?: number
  riskFinal?: number
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
  failureScenario?: FailureScenario
  impact?: Impact
  threat?: Threat
}

export type RiskResultWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  threatId?: number
  frequencyDensityPerLengthUnitYear?: number
  failureScenarioId?: number
  impactId?: number
  consequenceValue?: number
  riskDensityRUPerLengthYear?: number
  riskFinal?: number
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
  failureScenario?: FailureScenario
  impact?: Impact
  threat?: Threat
}

export type RiskResult = {
  id?: number
  assessmentId?: number
  assetId?: number
  threatId?: number
  frequencyDensityPerLengthUnitYear?: number
  failureScenarioId?: number
  impactId?: number
  consequenceValue?: number
  riskDensityRUPerLengthYear?: number
  riskFinal?: number
  forecastYear?: number
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  asset?: Asset
  failureScenario?: FailureScenario
  impact?: Impact
  threat?: Threat
}

export type RiskResultRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  threatId?: number
  frequencyDensityPerLengthUnitYear?: number
  failureScenarioId?: number
  impactId?: number
  consequenceValue?: number
  riskDensityRUPerLengthYear?: number
  riskFinal?: number
  forecastYear?: number
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  asset?: Asset
  failureScenario?: FailureScenario
  impact?: Impact
  threat?: Threat
}

export type Impact = {
  id?: number
  name?: string | null
  code?: string | null
  failureConsequenceInputs?: FailureConsequenceInput[] | null
  riskModels?: RiskModel[] | null
  riskResultWis?: RiskResultWis[] | null
  riskResults?: RiskResult[] | null
}

export type ColourByValueMinMax = {
  id?: number
  eventTableAttributeId?: number
  minValue?: number
  maxValue?: number
  eventTableAttribute?: EventTableAttribute
}

export type DataManagementCustomView = {
  id?: number
  dataViewName?: string | null
  dataViewDescription?: string | null
  isCurrentSystemData?: boolean | null
  dataViewFromDate?: string | null
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  dataManagementCustomViewDetails?: DataManagementCustomViewDetail[] | null
}

export type DataManagementCustomViewDetail = {
  id?: number
  eventTableAttributeId?: number
  attributeCustomAlias?: string | null
  dataManagementCustomViewId?: number
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  isSorted?: boolean | null
  isPinned?: boolean
  isHidden?: boolean
  dataManagementCustomView?: DataManagementCustomView
  dataManagementCustomViewNavigation?: EventTableAttribute
}

export type DataManagementCategory = {
  id?: number
  categoryName?: string | null
  dateCreated?: string
  dateModified?: string
  eventTables?: EventTable[] | null
}

export type EventTableAttributeDataQuality = {
  id?: number
  name?: string | null
  code?: string | null
  dataType?: string | null
  eventTableId?: number
  eventTableAttributeId?: number
  isExportAllowed?: boolean | null
  isImportAllowed?: boolean | null
  isDataViewAllowed?: boolean | null
  eventTypeTable?: string | null
  dateCreated?: string
  dateModified?: string
  isSorted?: boolean | null
  isPinned?: boolean
  isHidden?: boolean
  isCbvAllowed?: boolean | null
  isDeleted?: boolean
  eventTable?: EventTable
  eventTableAttribute?: EventTableAttribute
}

export type EventTable = {
  id?: number
  name?: string | null
  entity?: string | null
  dataManagementCategoryId?: number | null
  isExportAllowed?: boolean | null
  isImportAllowed?: boolean | null
  isDataViewAllowed?: boolean | null
  dateCreated?: string
  dateModified?: string
  arcGisUrl?: string | null
  typeTable?: string | null
  codeColumnName?: string | null
  typeCode?: string | null
  isDeleted?: boolean
  dataManagementCategory?: DataManagementCategory
  eventTableAttributeDataQualities?: EventTableAttributeDataQuality[] | null
  eventTableAttributes?: EventTableAttribute[] | null
}

export type ClassificationColour = {
  id?: number
  eventTableAttributeId?: number
  code?: string | null
  colour?: string | null
  name?: string | null
  eventTableAttributeEnum?: EventTableAttributeEnum
}

export type EventTableAttributeEnum = {
  id?: number
  eventTableAttributeId?: number
  code?: string | null
  name?: string | null
  classificationColours?: ClassificationColour[] | null
  eventTableAttribute?: EventTableAttribute
}

export type PointAssetAttribute = {
  id?: number
  name?: string | null
  code?: string | null
  dataType?: string | null
  dateCreated?: string
  dateModified?: string | null
  isSorted?: boolean | null
  isPinned?: boolean | null
  isHidden?: boolean | null
  unitTypeCode?: string | null
  isCbvAllowed?: boolean | null
  unitTypeCodeNavigation?: UnitType
}

export type UnitType = {
  code?: string | null
  name?: string | null
  continuousRouteAttributes?: ContinuousRouteAttribute[] | null
  eventTableAttributes?: EventTableAttribute[] | null
  pointAssetAttributes?: PointAssetAttribute[] | null
}

export type ContinuousRouteAttribute = {
  id?: number
  name?: string | null
  code?: string | null
  dataType?: string | null
  dateCreated?: string
  dateModified?: string | null
  isSorted?: boolean | null
  isPinned?: boolean | null
  isHidden?: boolean | null
  isCbvAllowed?: boolean | null
  unitTypeCode?: string | null
  continuousRouteAttributeRuleValidations?:
    | ContinuousRouteAttributeRuleValidation[]
    | null
  unitTypeCodeNavigation?: UnitType
}

export type ContinuousRouteAttributeRuleValidation = {
  id?: number
  continuousRouteAttributeId?: number
  validationRuleId?: number
  continuousRouteAttribute?: ContinuousRouteAttribute
  validationRule?: EventTableValidationRule
}

export type EventTableValidationRule = {
  id?: number
  name?: string | null
  continuousRouteAttributeRuleValidations?:
    | ContinuousRouteAttributeRuleValidation[]
    | null
  eventTableAttributeValidationRules?:
    | EventTableAttributeValidationRule[]
    | null
}

export type EventTableAttributeValidationRule = {
  id?: number
  eventTableAttributeId?: number
  validationRuleId?: number
  eventTableAttribute?: EventTableAttribute
  validationRule?: EventTableValidationRule
}

export type EventTableAttribute = {
  id?: number
  name?: string | null
  code?: string | null
  dataType?: string | null
  eventTableId?: number
  isExportAllowed?: boolean | null
  isImportAllowed?: boolean | null
  isDataViewAllowed?: boolean | null
  eventTypeTable?: string | null
  dateCreated?: string
  dateModified?: string
  isSorted?: boolean
  isPinned?: boolean
  isHidden?: boolean
  unitTypeCode?: string | null
  isCbvAllowed?: boolean
  isDeleted?: boolean
  colourByValueMinMaxes?: ColourByValueMinMax[] | null
  dataManagementCustomViewDetails?: DataManagementCustomViewDetail[] | null
  eventTable?: EventTable
  eventTableAttributeDataQualities?: EventTableAttributeDataQuality[] | null
  eventTableAttributeEnums?: EventTableAttributeEnum[] | null
  eventTableAttributeValidationRules?:
    | EventTableAttributeValidationRule[]
    | null
  riskModelAttributes?: RiskModelAttribute[] | null
  unitTypeCodeNavigation?: UnitType
}

export type RiskModelAttribute = {
  id?: number
  eventTableAttributeId?: number
  riskModelId?: number
  eventTableAttribute?: EventTableAttribute
  riskModel?: RiskModel
}

export type RiskModelType = {
  code?: string | null
  name?: string | null
  riskModelGroups?: RiskModelGroup[] | null
  riskModels?: RiskModel[] | null
}

export type AssetType = {
  code?: string | null
  name?: string | null
  assets?: Asset[] | null
  riskModelGroupAssetTypes?: RiskModelGroupAssetType[] | null
}

export type AssetTypeRead = {
  code?: string | null
  name?: string | null
  assets?: Asset[] | null
  riskModelGroupAssetTypes?: RiskModelGroupAssetType[] | null
}

export type RiskModelGroupAssetType = {
  id?: number
  riskModelGroupId?: number
  assetCode?: string | null
  assetCodeNavigation?: AssetType
  riskModelGroup?: RiskModelGroup
}

export type RiskModelGroup = {
  id?: number
  name?: string | null
  riskModelCode?: string | null
  version?: string | null
  assetCalculationGroups?: AssetCalculationGroup[] | null
  riskModelCodeNavigation?: RiskModelType
  riskModelGroupAssetTypes?: RiskModelGroupAssetType[] | null
  riskModels?: RiskModel[] | null
}

export type RiskModel = {
  id?: number
  name?: string | null
  typeCode?: string | null
  url?: string | null
  parameters?: string | null
  status?: number | null
  version?: string | null
  threatId?: number
  impactId?: number
  riskModelGroupId?: number
  assessmentRiskModels?: AssessmentRiskModel[] | null
  impact?: Impact
  riskModelAttributes?: RiskModelAttribute[] | null
  riskModelGroup?: RiskModelGroup
  threat?: Threat
  typeCodeNavigation?: RiskModelType
}

export type Threat = {
  id?: number
  name?: string | null
  timeDependent?: boolean
  code?: string | null
  isDeleted?: boolean
  failureProbabilityInputs?: FailureProbabilityInput[] | null
  riskModels?: RiskModel[] | null
  riskResultWis?: RiskResultWis[] | null
  riskResults?: RiskResult[] | null
}

export type FailureProbabilityInput = {
  id?: number
  fromForecastYear?: number | null
  toForecastYear?: number | null
  threatId?: number
  calculationGroupId?: number
  calculationGroup?: CalculationGroup
  failureProbabilityResults?: FailureProbabilityResult[] | null
  threat?: Threat
}

export type FailureProbabilityResult = {
  id?: number
  failureProbabiltyInputId?: number
  failureScenarioId?: number
  frequencyDensityPerLengthUnitYear?: number
  failureProbabiltyInput?: FailureProbabilityInput
  failureScenario?: FailureScenario
}

export type OutcomeScenario = {
  id?: number
  name?: string | null
  code?: string | null
  failureScenarios?: FailureScenario[] | null
}

export type ReleaseType = {
  id?: number
  name?: string | null
  code?: string | null
  failureScenarios?: FailureScenario[] | null
}

export type FailureScenario = {
  id?: number
  name?: string | null
  code?: string | null
  outcomeScenarioGroupId?: number | null
  releaseTypeGroupId?: number | null
  failureConsequenceResults?: FailureConsequenceResult[] | null
  failureProbabilityResults?: FailureProbabilityResult[] | null
  outcomeScenarioGroup?: OutcomeScenario
  releaseTypeGroup?: ReleaseType
  riskResultWis?: RiskResultWis[] | null
  riskResults?: RiskResult[] | null
}

export type FailureConsequenceResult = {
  id?: number
  failureConsequenceInputId?: number
  failureScenarioId?: number
  consequenceValue?: number
  failureConsequenceInput?: FailureConsequenceInput
  failureScenario?: FailureScenario
}

export type FailureConsequenceInput = {
  id?: number
  impactId?: number | null
  calculationGroupId?: number
  calculationGroup?: CalculationGroup
  failureConsequenceResults?: FailureConsequenceResult[] | null
  impact?: Impact
}

export type CalculationGroup = {
  id?: number
  code?: string | null
  assetCalculationGroups?: AssetCalculationGroup[] | null
  failureConsequenceInputs?: FailureConsequenceInput[] | null
  failureProbabilityInputs?: FailureProbabilityInput[] | null
}

export type AssetCalculationGroup = {
  id?: number
  assetId?: number
  assessmentId?: number
  riskModelGroupId?: number
  calculationGroupCode?: string | null
  calculationGroupId?: number | null
  assessment?: Assessment
  asset?: Asset
  calculationGroup?: CalculationGroup
  riskModelGroup?: RiskModelGroup
}

export type AssetCalculationGroupRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  riskModelGroupId?: number
  calculationGroupCode?: string | null
  calculationGroupId?: number | null
  assessment?: Assessment
  asset?: Asset
  calculationGroup?: CalculationGroup
  riskModelGroup?: RiskModelGroup
}

export type BendType = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type BendTypeRead = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type BendingLocationType = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type BendingLocationTypeRead = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type BendingMethodType = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type BendingMethodTypeRead = {
  code?: string | null
  name?: string | null
  bends?: Bend[] | null
}

export type CouplingType = {
  code?: string | null
  name?: string | null
  couplings?: Coupling[] | null
}

export type CouplingTypeRead = {
  code?: string | null
  name?: string | null
  couplings?: Coupling[] | null
}

export type FlangeSealType = {
  code?: string | null
  name?: string | null
  flanges?: Flange[] | null
}

export type Flange = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  flangeSealTypeCode?: string | null
  sleeveInstalledFlag?: boolean | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  installationDate?: string | null
  wallThickness?: number | null
  asset?: Asset
  flangeSealTypeCodeNavigation?: FlangeSealType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
}

export type FlangeRead = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  flangeSealTypeCode?: string | null
  sleeveInstalledFlag?: boolean | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  installationDate?: string | null
  wallThickness?: number | null
  asset?: Asset
  flangeSealTypeCodeNavigation?: FlangeSealType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
}

export type DataQualityCode = {
  code?: string | null
  name?: string | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
}

export type DataQualityCodeRead = {
  code?: string | null
  name?: string | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
}

export type SystemScopeContinuousRoute = {
  id?: number
  systemScopeId?: number
  continuousRouteId?: number
  continuousRoute?: ContinuousRoute
  systemScope?: SystemScope
}

export type SystemScopeContinuousRouteRead = {
  id?: number
  systemScopeId?: number
  continuousRouteId?: number
  continuousRoute?: ContinuousRoute
  systemScope?: SystemScope
}

export type SystemScopeRegion = {
  id?: number
  regionCode?: string | null
  systemScopeId?: number
  dateCreated?: string
  dateModified?: string
  regionId?: number | null
  systemScope?: SystemScope
}

export type SystemScopeRegionRead = {
  id?: number
  regionCode?: string | null
  systemScopeId?: number
  dateCreated?: string
  dateModified?: string
  regionId?: number | null
  systemScope?: SystemScope
}

export type SystemScopeState = {
  id?: number
  systemScopeId?: number
  stateCode?: string | null
  dateCreated?: string
  dateModified?: string
  stateCodeNavigation?: StateType
  systemScope?: SystemScope
}

export type SystemScopeStateRead = {
  id?: number
  systemScopeId?: number
  stateCode?: string | null
  dateCreated?: string
  dateModified?: string
  stateCodeNavigation?: StateType
  systemScope?: SystemScope
}

export type SystemScopeUser = {
  id?: number
  userId?: string
  systemScopeId?: number
  dateCreated?: string
  dateModified?: string
  systemScope?: SystemScope
}

export type SystemScopeUserRead = {
  id?: number
  userId?: string
  systemScopeId?: number
  dateCreated?: string
  dateModified?: string
  systemScope?: SystemScope
}

export type ColourByValue = {
  id?: number
  userId?: string
  feature?: string | null
  tableId?: number
  configuration?: string | null
  user?: User
}

export type SavedStateContinuousRoute = {
  id?: number
  savedStateId?: number
  continuousRouteId?: number
  continuousRoute?: ContinuousRoute
  savedState?: SavedState
}

export type SavedStateContinuousRouteRead = {
  id?: number
  savedStateId?: number
  continuousRouteId?: number
  continuousRoute?: ContinuousRoute
  savedState?: SavedState
}

export type SavedState = {
  id?: number
  name?: string | null
  baselineId?: number
  dateCreated?: string
  dateModified?: string | null
  createdBy?: string
  modifiedBy?: string | null
  assessments?: Assessment[] | null
  createdByNavigation?: User
  modifiedByNavigation?: User
  savedStateContinuousRoutes?: SavedStateContinuousRoute[] | null
}

export type SavedStateRead = {
  id?: number
  name?: string | null
  baselineId?: number
  dateCreated?: string
  dateModified?: string | null
  createdBy?: string
  modifiedBy?: string | null
  assessments?: Assessment[] | null
  createdByNavigation?: User
  modifiedByNavigation?: User
  savedStateContinuousRoutes?: SavedStateContinuousRouteRead[] | null
}

export type User = {
  id?: string
  authId?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  isActive?: boolean
  dateCreated?: string
  dateModified?: string
  colourByValues?: ColourByValue[] | null
  savedStateCreatedByNavigations?: SavedState[] | null
  savedStateModifiedByNavigations?: SavedState[] | null
  systemScopes?: SystemScope[] | null
}

export type UserRead = {
  id?: string
  authId?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  isActive?: boolean
  dateCreated?: string
  dateModified?: string
  colourByValues?: ColourByValue[] | null
  savedStateCreatedByNavigations?: SavedStateRead[] | null
  savedStateModifiedByNavigations?: SavedStateRead[] | null
  systemScopes?: SystemScope[] | null
}

export type SystemScope = {
  id?: number
  name?: string | null
  isPrivate?: boolean | null
  customQuery?: string | null
  userId?: string | null
  isActive?: boolean | null
  dateCreated?: string
  dateModified?: string
  createdBy?: string | null
  modifiedBy?: string | null
  isDefault?: boolean
  systemScopeContinuousRoutes?: SystemScopeContinuousRoute[] | null
  systemScopeOperators?: SystemScopeOperator[] | null
  systemScopeRegions?: SystemScopeRegion[] | null
  systemScopeStates?: SystemScopeState[] | null
  systemScopeUsers?: SystemScopeUser[] | null
  user?: User
}

export type SystemScopeRead = {
  id?: number
  name?: string | null
  isPrivate?: boolean | null
  customQuery?: string | null
  userId?: string | null
  isActive?: boolean | null
  dateCreated?: string
  dateModified?: string
  createdBy?: string | null
  modifiedBy?: string | null
  isDefault?: boolean
  systemScopeContinuousRoutes?: SystemScopeContinuousRouteRead[] | null
  systemScopeOperators?: SystemScopeOperator[] | null
  systemScopeRegions?: SystemScopeRegionRead[] | null
  systemScopeStates?: SystemScopeStateRead[] | null
  systemScopeUsers?: SystemScopeUserRead[] | null
  user?: UserRead
}

export type SystemScopeOperator = {
  id?: number
  systemScopeId?: number
  operatorCode?: string | null
  dateCreated?: string
  dateModified?: string
  operatorId?: number | null
  operatorCodeNavigation?: OperatorType
  systemScope?: SystemScope
}

export type SystemScopeOperatorRead = {
  id?: number
  systemScopeId?: number
  operatorCode?: string | null
  dateCreated?: string
  dateModified?: string
  operatorId?: number | null
  operatorCodeNavigation?: OperatorType
  systemScope?: SystemScopeRead
}

export type OperatorType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
  pipes?: Pipe[] | null
  systemScopeOperators?: SystemScopeOperator[] | null
}

export type OperatorTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
  pipes?: Pipe[] | null
  systemScopeOperators?: SystemScopeOperatorRead[] | null
}

export type PhmsaBaselineStatus = {
  code?: string | null
  description?: string | null
  dateCreated?: string
  dateModified?: string
  phmsaBaselines?: PhmsaBaseline[] | null
}

export type PhmsaBaseline = {
  id?: number
  fromDate?: string
  toDate?: string | null
  statusCode?: string | null
  phmsaBaselineSegments?: PhmsaBaselineSegment[] | null
  statusCodeNavigation?: PhmsaBaselineStatus
}

export type PhmsaBaselineSegment = {
  id?: number
  baselineId?: number
  phmsaSegmentId?: number
  baseline?: PhmsaBaseline
  phmsaSegment?: PhmsaSegment
}

export type AssetStatus = {
  code?: string | null
  description?: string | null
  assets?: Asset[] | null
  phmsaSegments?: PhmsaSegment[] | null
}

export type AssetStatusRead = {
  code?: string | null
  description?: string | null
  assets?: Asset[] | null
  phmsaSegments?: PhmsaSegment[] | null
}

export type PhmsaSegment = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string | null
  toDate?: string | null
  statusCode?: string | null
  continuousRoute?: ContinuousRoute
  phmsaBaselineSegments?: PhmsaBaselineSegment[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
  statusCodeNavigation?: AssetStatus
}

export type PhmsaSegmentRead = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string | null
  toDate?: string | null
  statusCode?: string | null
  continuousRoute?: ContinuousRoute
  phmsaBaselineSegments?: PhmsaBaselineSegment[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
  statusCodeNavigation?: AssetStatusRead
}

export type PhmsaSegmentAttribute = {
  id?: number
  phmsaSegmentId?: number
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  commodityCode?: string | null
  consequenceAreaCode?: string | null
  offShorePipeFlag?: boolean | null
  pipelineCode?: string | null
  materialCode?: string | null
  coatingTypeCode?: string | null
  nominalPipeSize?: number | null
  installationDate?: string
  operatingStress?: number
  classLocationCode?: string | null
  stateCode?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  createdBy?: string | null
  modifiedBy?: string | null
  cpCriteriaMetFlag?: boolean
  smartPiggableFlag?: boolean
  piggableFlag?: boolean
  maop?: number
  determinationMethod?: string | null
  completeFlag?: boolean
  testPressure?: number | null
  operatingStressDq?: string | null
  operatingStressDqNavigation?: DataQualityCode
  operatorCodeNavigation?: OperatorType
  phmsaSegment?: PhmsaSegment
  stateCodeNavigation?: StateType
}

export type PhmsaSegmentAttributeRead = {
  id?: number
  phmsaSegmentId?: number
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  commodityCode?: string | null
  consequenceAreaCode?: string | null
  offShorePipeFlag?: boolean | null
  pipelineCode?: string | null
  materialCode?: string | null
  coatingTypeCode?: string | null
  nominalPipeSize?: number | null
  installationDate?: string
  operatingStress?: number
  classLocationCode?: string | null
  stateCode?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  createdBy?: string | null
  modifiedBy?: string | null
  cpCriteriaMetFlag?: boolean
  smartPiggableFlag?: boolean
  piggableFlag?: boolean
  maop?: number
  determinationMethod?: string | null
  completeFlag?: boolean
  testPressure?: number | null
  operatingStressDq?: string | null
  operatingStressDqNavigation?: DataQualityCodeRead
  operatorCodeNavigation?: OperatorTypeRead
  phmsaSegment?: PhmsaSegmentRead
  stateCodeNavigation?: StateType
}

export type InspectionFindingType = {
  code?: string | null
  name?: string | null
  assessmentPipeBottomDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougeDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougeIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougedDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougedDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeExternalCorrosionDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeExternalCorrosionIliFndgCodeNavigations?:
    | AssessmentPipe[]
    | null
  assessmentPipeGirthWeldDefectCiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeGirthWeldDefectDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeGirthWeldDefectIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeHardSpotDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeHardSpotIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSccAnomalyDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSccAnomalyIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSeamWeldDefectDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSeamWeldDefectIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSswcDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSswcIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougeDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougeIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougedDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougedDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  pipeBottomDentDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomDentIliFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougeDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougeIliFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougedDentDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougedDentIliFndgCodeNavigations?: Pipe[] | null
  pipeExternalCorrosionDiFndgCodeNavigations?: Pipe[] | null
  pipeExternalCorrosionIliFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectCiFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectDiFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectIliFndgCodeNavigations?: Pipe[] | null
  pipeHardSpotDiFndgCodeNavigations?: Pipe[] | null
  pipeHardSpotIliFndgCodeNavigations?: Pipe[] | null
  pipeSccAnomalyDiFndgCodeNavigations?: Pipe[] | null
  pipeSccAnomalyIliFndgCodeNavigations?: Pipe[] | null
  pipeSeamWeldDefectDiFndgCodeNavigations?: Pipe[] | null
  pipeSeamWeldDefectIliFndgCodeNavigations?: Pipe[] | null
  pipeSswcDiFndgCodeNavigations?: Pipe[] | null
  pipeSswcIliFndgCodeNavigations?: Pipe[] | null
  pipeTopDentDiFndgCodeNavigations?: Pipe[] | null
  pipeTopDentIliFndgCodeNavigations?: Pipe[] | null
  pipeTopGougeDiFndgCodeNavigations?: Pipe[] | null
  pipeTopGougeIliFndgCodeNavigations?: Pipe[] | null
  pipeTopGougedDentDiFndgCodeNavigations?: Pipe[] | null
  pipeTopGougedDentIliFndgCodeNavigations?: Pipe[] | null
  sleeves?: Sleeve[] | null
}

export type InspectionFindingTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipeBottomDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougeDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougeIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougedDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeBottomGougedDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeExternalCorrosionDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeExternalCorrosionIliFndgCodeNavigations?:
    | AssessmentPipe[]
    | null
  assessmentPipeGirthWeldDefectCiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeGirthWeldDefectDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeGirthWeldDefectIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeHardSpotDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeHardSpotIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSccAnomalyDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSccAnomalyIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSeamWeldDefectDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSeamWeldDefectIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSswcDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeSswcIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougeDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougeIliFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougedDentDiFndgCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeTopGougedDentIliFndgCodeNavigations?: AssessmentPipe[] | null
  pipeBottomDentDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomDentIliFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougeDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougeIliFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougedDentDiFndgCodeNavigations?: Pipe[] | null
  pipeBottomGougedDentIliFndgCodeNavigations?: Pipe[] | null
  pipeExternalCorrosionDiFndgCodeNavigations?: Pipe[] | null
  pipeExternalCorrosionIliFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectCiFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectDiFndgCodeNavigations?: Pipe[] | null
  pipeGirthWeldDefectIliFndgCodeNavigations?: Pipe[] | null
  pipeHardSpotDiFndgCodeNavigations?: Pipe[] | null
  pipeHardSpotIliFndgCodeNavigations?: Pipe[] | null
  pipeSccAnomalyDiFndgCodeNavigations?: Pipe[] | null
  pipeSccAnomalyIliFndgCodeNavigations?: Pipe[] | null
  pipeSeamWeldDefectDiFndgCodeNavigations?: Pipe[] | null
  pipeSeamWeldDefectIliFndgCodeNavigations?: Pipe[] | null
  pipeSswcDiFndgCodeNavigations?: Pipe[] | null
  pipeSswcIliFndgCodeNavigations?: Pipe[] | null
  pipeTopDentDiFndgCodeNavigations?: Pipe[] | null
  pipeTopDentIliFndgCodeNavigations?: Pipe[] | null
  pipeTopGougeDiFndgCodeNavigations?: Pipe[] | null
  pipeTopGougeIliFndgCodeNavigations?: Pipe[] | null
  pipeTopGougedDentDiFndgCodeNavigations?: Pipe[] | null
  pipeTopGougedDentIliFndgCodeNavigations?: Pipe[] | null
  sleeves?: Sleeve[] | null
}

export type SleeveType = {
  code?: string | null
  name?: string | null
  sleeves?: Sleeve[] | null
}

export type Sleeve = {
  id?: number
  assetId?: number
  operatingPressure?: number | null
  outsideDiameter?: number | null
  sleeveAnomalyCiFndgCode?: string | null
  sleeveTypeCode?: string | null
  wallThickness?: number | null
  yieldStrength?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  inletOutsideDiameter?: number | null
  inspectedFlag?: boolean | null
  installationDate?: string | null
  outletOutsideDiameter?: number | null
  asset?: Asset
  regionCodeNavigation?: RegionType
  sleeveAnomalyCiFndgCodeNavigation?: InspectionFindingType
  sleeveTypeCodeNavigation?: SleeveType
  stateCodeNavigation?: StateType
}

export type SleeveRead = {
  id?: number
  assetId?: number
  operatingPressure?: number | null
  outsideDiameter?: number | null
  sleeveAnomalyCiFndgCode?: string | null
  sleeveTypeCode?: string | null
  wallThickness?: number | null
  yieldStrength?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  inletOutsideDiameter?: number | null
  inspectedFlag?: boolean | null
  installationDate?: string | null
  outletOutsideDiameter?: number | null
  asset?: Asset
  regionCodeNavigation?: RegionType
  sleeveAnomalyCiFndgCodeNavigation?: InspectionFindingTypeRead
  sleeveTypeCodeNavigation?: SleeveType
  stateCodeNavigation?: StateType
}

export type LandUseType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
  valves?: Valve[] | null
}

export type LandUseTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
  valves?: Valve[] | null
}

export type PressureRatingType = {
  code?: string | null
  name?: string | null
  valves?: Valve[] | null
}

export type ValveFunctionType = {
  code?: string | null
  name?: string | null
  valves?: Valve[] | null
}

export type ValveType = {
  code?: string | null
  name?: string | null
  valves?: Valve[] | null
}

export type Valve = {
  id?: number
  assetId?: number
  inletOutsideDiameter?: number | null
  landUseCode?: string | null
  outletOutsideDiameter?: number | null
  patrolInterval?: number | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  roadCrossingFlag?: boolean | null
  valveTypeCode?: string | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  installationDate?: string | null
  name?: string | null
  outsideDiameter?: number | null
  pressureRating?: number | null
  pressureRatingCode?: string | null
  valveFunctionTypeCode?: string | null
  wallThickness?: number | null
  asset?: Asset
  landUseCodeNavigation?: LandUseType
  pressureRatingCodeNavigation?: PressureRatingType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
  valveFunctionTypeCodeNavigation?: ValveFunctionType
  valveTypeCodeNavigation?: ValveType
}

export type ValveRead = {
  id?: number
  assetId?: number
  inletOutsideDiameter?: number | null
  landUseCode?: string | null
  outletOutsideDiameter?: number | null
  patrolInterval?: number | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  roadCrossingFlag?: boolean | null
  valveTypeCode?: string | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  installationDate?: string | null
  name?: string | null
  outsideDiameter?: number | null
  pressureRating?: number | null
  pressureRatingCode?: string | null
  valveFunctionTypeCode?: string | null
  wallThickness?: number | null
  asset?: Asset
  landUseCodeNavigation?: LandUseTypeRead
  pressureRatingCodeNavigation?: PressureRatingType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
  valveFunctionTypeCodeNavigation?: ValveFunctionType
  valveTypeCodeNavigation?: ValveType
}

export type StateType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  bends?: Bend[] | null
  couplings?: Coupling[] | null
  flanges?: Flange[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttribute[] | null
  pipes?: Pipe[] | null
  sleeves?: Sleeve[] | null
  systemScopeStates?: SystemScopeState[] | null
  valves?: Valve[] | null
}

export type StateTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  bends?: Bend[] | null
  couplings?: Coupling[] | null
  flanges?: FlangeRead[] | null
  phmsaSegmentAttributes?: PhmsaSegmentAttributeRead[] | null
  pipes?: Pipe[] | null
  sleeves?: SleeveRead[] | null
  systemScopeStates?: SystemScopeStateRead[] | null
  valves?: ValveRead[] | null
}

export type Coupling = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  couplingTypeCode?: string | null
  earthMvmntSusceptibility?: number | null
  floodSusceptibility?: number | null
  installationDate?: string | null
  operatingPressure?: number | null
  outsideDiameter?: number | null
  sleeveInstalledFlag?: boolean | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  wallThickness?: number | null
  yieldStrength?: number | null
  asset?: Asset
  couplingTypeCodeNavigation?: CouplingType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
}

export type CouplingRead = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  couplingTypeCode?: string | null
  earthMvmntSusceptibility?: number | null
  floodSusceptibility?: number | null
  installationDate?: string | null
  operatingPressure?: number | null
  outsideDiameter?: number | null
  sleeveInstalledFlag?: boolean | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  wallThickness?: number | null
  yieldStrength?: number | null
  asset?: Asset
  couplingTypeCodeNavigation?: CouplingTypeRead
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateTypeRead
}

export type RegionType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  bends?: Bend[] | null
  couplings?: Coupling[] | null
  flanges?: Flange[] | null
  pipes?: Pipe[] | null
  sleeves?: Sleeve[] | null
  valves?: Valve[] | null
}

export type RegionTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  bends?: Bend[] | null
  couplings?: CouplingRead[] | null
  flanges?: FlangeRead[] | null
  pipes?: Pipe[] | null
  sleeves?: SleeveRead[] | null
  valves?: ValveRead[] | null
}

export type Bend = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  bendTypeCode?: string | null
  bendingLocationCode?: string | null
  bendingMethodCode?: string | null
  earthMvmntSusceptibility?: number | null
  floodSusceptibility?: number | null
  inletOutsideDiameter?: number | null
  installationDate?: string | null
  operatingPressure?: number | null
  outletOutsideDiameter?: number | null
  reassessmentInterval?: number | null
  testDate?: string | null
  wallThickness?: number | null
  wrinklePresentFlag?: boolean | null
  yieldStrength?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  outsideDiameter?: number | null
  asset?: Asset
  bendTypeCodeNavigation?: BendType
  bendingLocationCodeNavigation?: BendingLocationType
  bendingMethodCodeNavigation?: BendingMethodType
  regionCodeNavigation?: RegionType
  stateCodeNavigation?: StateType
}

export type BendRead = {
  id?: number
  assetId?: number
  anchorInstalledFlag?: boolean | null
  bendTypeCode?: string | null
  bendingLocationCode?: string | null
  bendingMethodCode?: string | null
  earthMvmntSusceptibility?: number | null
  floodSusceptibility?: number | null
  inletOutsideDiameter?: number | null
  installationDate?: string | null
  operatingPressure?: number | null
  outletOutsideDiameter?: number | null
  reassessmentInterval?: number | null
  testDate?: string | null
  wallThickness?: number | null
  wrinklePresentFlag?: boolean | null
  yieldStrength?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  operatingTemperature?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  roadCrossingFlag?: boolean | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  outsideDiameter?: number | null
  asset?: Asset
  bendTypeCodeNavigation?: BendTypeRead
  bendingLocationCodeNavigation?: BendingLocationTypeRead
  bendingMethodCodeNavigation?: BendingMethodTypeRead
  regionCodeNavigation?: RegionTypeRead
  stateCodeNavigation?: StateTypeRead
}

export type RiskTotalByAssetWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByAssetWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByAsset = {
  id?: number
  assetId?: number
  assessmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByAssetRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByFailureScenarioWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  failureScenarioId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByFailureScenarioWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  failureScenarioId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByFailureScenario = {
  id?: number
  assetId?: number
  assessmentId?: number
  failureScenarioId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByFailureScenarioRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  failureScenarioId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByImpactWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByImpactWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByImpact = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByImpactRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByThreatWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByThreatWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByThreat = {
  id?: number
  assetId?: number
  assessmentId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalByThreatRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByImpactWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByImpactWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByImpact = {
  id?: number
  assetId?: number
  assessmentId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByImpactRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  forecastYear?: number
  impactCommodityLoss?: number | null
  impactCorporateImage?: number | null
  impactEnvironmentalImpact?: number | null
  impactFatality?: number | null
  impactInjury?: number | null
  impactPropertyDamage?: number | null
  impactRegulatoryPenalty?: number | null
  impactRepair?: number | null
  impactResponse?: number | null
  impactServiceDisruption?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByThreatWis = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByThreatWisRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  forecastYear?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByThreat = {
  id?: number
  assetId?: number
  assessmentId?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type RiskTotalDensityByThreatRead = {
  id?: number
  assetId?: number
  assessmentId?: number
  threatBend?: number | null
  threatBlasting?: number | null
  threatConstructionDefect?: number | null
  threatCoupling?: number | null
  threatEarthMovement?: number | null
  threatExcavation?: number | null
  threatExternalCorrosion?: number | null
  threatExternalValveInterference?: number | null
  threatFlange?: number | null
  threatFlood?: number | null
  threatFrostHeave?: number | null
  threatGirthWeld?: number | null
  threatIncorrectOperation?: number | null
  threatIncorrectValveOperation?: number | null
  threatInternalCorrosion?: number | null
  threatLandVehicle?: number | null
  threatLandslide?: number | null
  threatLatentDamage?: number | null
  threatLightning?: number | null
  threatManufacturingDefect?: number | null
  threatOtherLoad?: number | null
  threatSeamWeld?: number | null
  threatSeismicActivity?: number | null
  threatSleeve?: number | null
  threatStressCorrosionCracking?: number | null
  threatSubsidence?: number | null
  threatValveEquipmentFailure?: number | null
  threatVandalism?: number | null
  threatWatercraft?: number | null
  forecastYear?: number
  assessment?: Assessment
  asset?: Asset
}

export type BaselineStatus = {
  code?: string | null
  description?: string | null
  baselines?: Baseline[] | null
}

export type Baseline = {
  id?: number
  fromDate?: string | null
  toDate?: string | null
  statusCode?: string | null
  baselineServiceUrl?: string | null
  wisLayerServiceUrl?: string | null
  baselineLayerName?: string | null
  wisLayerName?: string | null
  baselineSegments?: BaselineSegment[] | null
  statusCodeNavigation?: BaselineStatus
}

export type BaselineSegment = {
  id?: number
  baselineId?: number
  segmentId?: number
  baseline?: Baseline
  segment?: Segment
}

export type Segment = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  baselineSegments?: BaselineSegment[] | null
  continuousRoute?: ContinuousRoute
  segmentAssets?: SegmentAsset[] | null
}

export type SegmentRead = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  baselineSegments?: BaselineSegment[] | null
  continuousRoute?: ContinuousRoute
  segmentAssets?: SegmentAsset[] | null
}

export type SegmentAsset = {
  id?: number
  segmentId?: number
  assetId?: number
  proportion?: number
  asset?: Asset
  segment?: Segment
}

export type SegmentAssetRead = {
  id?: number
  segmentId?: number
  assetId?: number
  proportion?: number
  asset?: Asset
  segment?: SegmentRead
}

export type PrecisionModels = 0 | 1 | 2

export type PrecisionModel = {
  scale?: number
  precisionModelType?: PrecisionModels
}

export type PrecisionModelRead = {
  isFloating?: boolean
  maximumSignificantDigits?: number
  scale?: number
  gridSize?: number
  precisionModelType?: PrecisionModels
}

export type Ordinates =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 7
  | 8
  | 16
  | 32
  | 64
  | 128
  | 256
  | 512
  | 1024
  | 2048
  | 4096
  | 8192
  | 16384
  | 32768
  | 65535
  | 65536
  | 65539
  | 65543
  | 131072
  | 262144
  | 524288
  | 1048576
  | 2097152
  | 4194304
  | 8388608
  | 16777216
  | 33554432
  | 67108864
  | 134217728
  | 268435456
  | 536870912
  | 1073741824
  | -2147483648
  | -65536
  | -1

export type CoordinateSequenceFactory = {
  ordinates?: Ordinates
}

export type GeometryOverlay = object

export type CoordinateEqualityComparer = object

export type NtsGeometryServices = {
  geometryOverlay?: GeometryOverlay
  coordinateEqualityComparer?: CoordinateEqualityComparer
  defaultCoordinateSequenceFactory?: CoordinateSequenceFactory
  defaultPrecisionModel?: PrecisionModel
}

export type NtsGeometryServicesRead = {
  geometryOverlay?: GeometryOverlay
  coordinateEqualityComparer?: CoordinateEqualityComparer
  defaultSRID?: number
  defaultCoordinateSequenceFactory?: CoordinateSequenceFactory
  defaultPrecisionModel?: PrecisionModelRead
}

export type GeometryFactory = {
  precisionModel?: PrecisionModel
  coordinateSequenceFactory?: CoordinateSequenceFactory
  geometryServices?: NtsGeometryServices
}

export type GeometryFactoryRead = {
  precisionModel?: PrecisionModelRead
  coordinateSequenceFactory?: CoordinateSequenceFactory
  srid?: number
  geometryServices?: NtsGeometryServicesRead
}

export type OgcGeometryType =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16

export type Coordinate = {
  x?: number
  y?: number
  z?: number
  m?: number
  coordinateValue?: Coordinate
}

export type CoordinateRead = {
  x?: number
  y?: number
  z?: number
  m?: number
  coordinateValue?: CoordinateRead
  isValid?: boolean
}

export type Envelope = {
  centre?: Coordinate
}

export type EnvelopeRead = {
  isNull?: boolean
  width?: number
  height?: number
  diameter?: number
  minX?: number
  maxX?: number
  minY?: number
  maxY?: number
  area?: number
  minExtent?: number
  maxExtent?: number
  centre?: CoordinateRead
}

export type CoordinateSequence = {
  ordinates?: Ordinates
  first?: Coordinate
  last?: Coordinate
}

export type CoordinateSequenceRead = {
  dimension?: number
  measures?: number
  spatial?: number
  ordinates?: Ordinates
  hasZ?: boolean
  hasM?: boolean
  zOrdinateIndex?: number
  mOrdinateIndex?: number
  first?: CoordinateRead
  last?: CoordinateRead
  count?: number
}

export type Dimension = 0 | 1 | 2 | 3 | -3 | -2 | -1

export type Point = {
  factory?: GeometryFactory
  userData?: any | null
  srid?: number
  precisionModel?: PrecisionModel
  centroid?: Point
  interiorPoint?: Point
  pointOnSurface?: Point
  envelope?: Geometry
  envelopeInternal?: Envelope
  coordinateSequence?: CoordinateSequence
  dimension?: Dimension
  boundaryDimension?: Dimension
  x?: number
  y?: number
  coordinate?: Coordinate
  ogcGeometryType?: OgcGeometryType
  boundary?: Geometry
  z?: number
  m?: number
}

export type PointRead = {
  factory?: GeometryFactoryRead
  userData?: any | null
  srid?: number
  precisionModel?: PrecisionModelRead
  numGeometries?: number
  isSimple?: boolean
  isValid?: boolean
  area?: number
  length?: number
  centroid?: PointRead
  interiorPoint?: PointRead
  pointOnSurface?: PointRead
  envelope?: Geometry
  envelopeInternal?: EnvelopeRead
  isRectangle?: boolean
  coordinateSequence?: CoordinateSequenceRead
  coordinates?: CoordinateRead[] | null
  numPoints?: number
  isEmpty?: boolean
  dimension?: Dimension
  boundaryDimension?: Dimension
  x?: number
  y?: number
  coordinate?: CoordinateRead
  geometryType?: string | null
  ogcGeometryType?: OgcGeometryType
  boundary?: Geometry
  z?: number
  m?: number
}

export type Geometry = {
  factory?: GeometryFactory
  userData?: any | null
  srid?: number
  ogcGeometryType?: OgcGeometryType
  precisionModel?: PrecisionModel
  coordinate?: Coordinate
  centroid?: Point
  interiorPoint?: Point
  pointOnSurface?: Point
  dimension?: Dimension
  boundary?: Geometry
  boundaryDimension?: Dimension
  envelope?: Geometry
  envelopeInternal?: Envelope
}

export type GeometryRead = {
  factory?: GeometryFactoryRead
  userData?: any | null
  srid?: number
  geometryType?: string | null
  ogcGeometryType?: OgcGeometryType
  precisionModel?: PrecisionModelRead
  coordinate?: CoordinateRead
  coordinates?: CoordinateRead[] | null
  numPoints?: number
  numGeometries?: number
  isSimple?: boolean
  isValid?: boolean
  isEmpty?: boolean
  area?: number
  length?: number
  centroid?: PointRead
  interiorPoint?: PointRead
  pointOnSurface?: PointRead
  dimension?: Dimension
  boundary?: GeometryRead
  boundaryDimension?: Dimension
  envelope?: GeometryRead
  envelopeInternal?: EnvelopeRead
  isRectangle?: boolean
}

export type StructureLookUp = {
  id?: number
  structureTypeCode?: string | null
  dayInsideTimeFraction?: number
  dayOutsideTimeFraction?: number
  nightInsideTimeFraction?: number
  nightOutsideTimeFraction?: number
  woodenStructureFlag?: boolean
  valuationUsdPerSqFt?: number
  populationPerUnit?: number | null
  populationPerSqFt?: number | null
  mobilityFactor?: number
  footprintSqFt?: number
  structureTypeCodeNavigation?: StructureType
}

export type StructureType = {
  code?: string | null
  name?: string | null
  structureLookUps?: StructureLookUp[] | null
  structures?: Structure[] | null
  structuresNearAssets?: StructuresNearAsset[] | null
}

export type StructureTypeRead = {
  code?: string | null
  name?: string | null
  structureLookUps?: StructureLookUp[] | null
  structures?: Structure[] | null
  structuresNearAssets?: StructuresNearAsset[] | null
}

export type Structure = {
  id?: number
  geom?: Geometry
  structureTypeCode?: string | null
  humanOccupancyFlag?: boolean
  identifiedSiteFlag?: boolean
  units?: number
  occupantCount?: number | null
  stories?: number
  valuationUsd?: number | null
  mobilityFactor?: number | null
  woodenStructureFlag?: boolean | null
  dayInsideTimeFraction?: number | null
  dayOutsideTimeFraction?: number | null
  nightInsideTimeFraction?: number | null
  nightOutsideTimeFraction?: number | null
  dataSource?: string | null
  fromDate?: string
  toDate?: string | null
  structureTypeCodeNavigation?: StructureType
  structuresNearAssets?: StructuresNearAsset[] | null
}

export type StructureRead = {
  id?: number
  geom?: GeometryRead
  structureTypeCode?: string | null
  humanOccupancyFlag?: boolean
  identifiedSiteFlag?: boolean
  units?: number
  occupantCount?: number | null
  stories?: number
  valuationUsd?: number | null
  mobilityFactor?: number | null
  woodenStructureFlag?: boolean | null
  dayInsideTimeFraction?: number | null
  dayOutsideTimeFraction?: number | null
  nightInsideTimeFraction?: number | null
  nightOutsideTimeFraction?: number | null
  dataSource?: string | null
  fromDate?: string
  toDate?: string | null
  structureTypeCodeNavigation?: StructureTypeRead
  structuresNearAssets?: StructuresNearAsset[] | null
}

export type StructuresNearAsset = {
  id?: number
  assetId?: number
  structureTypeCode?: string | null
  structureId?: number
  bufferSizeFt?: number
  processedDate?: string
  distanceFromAssetFt?: number
  asset?: Asset
  structure?: Structure
  structureTypeCodeNavigation?: StructureType
}

export type StructuresNearAssetRead = {
  id?: number
  assetId?: number
  structureTypeCode?: string | null
  structureId?: number
  bufferSizeFt?: number
  processedDate?: string
  distanceFromAssetFt?: number
  asset?: Asset
  structure?: StructureRead
  structureTypeCodeNavigation?: StructureTypeRead
}

export type Asset = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  statusCode?: string | null
  assetCode?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  assetCalculationGroups?: AssetCalculationGroup[] | null
  assetCodeNavigation?: AssetType
  bends?: Bend[] | null
  continuousRoute?: ContinuousRoute
  couplings?: Coupling[] | null
  flanges?: Flange[] | null
  pipes?: Pipe[] | null
  riskResultWis?: RiskResultWis[] | null
  riskResults?: RiskResult[] | null
  riskTotalByAssetWis?: RiskTotalByAssetWis[] | null
  riskTotalByAssets?: RiskTotalByAsset[] | null
  riskTotalByFailureScenarioWis?: RiskTotalByFailureScenarioWis[] | null
  riskTotalByFailureScenarios?: RiskTotalByFailureScenario[] | null
  riskTotalByImpactWis?: RiskTotalByImpactWis[] | null
  riskTotalByImpacts?: RiskTotalByImpact[] | null
  riskTotalByThreatWis?: RiskTotalByThreatWis[] | null
  riskTotalByThreats?: RiskTotalByThreat[] | null
  riskTotalDensityByImpactWis?: RiskTotalDensityByImpactWis[] | null
  riskTotalDensityByImpacts?: RiskTotalDensityByImpact[] | null
  riskTotalDensityByThreatWis?: RiskTotalDensityByThreatWis[] | null
  riskTotalDensityByThreats?: RiskTotalDensityByThreat[] | null
  segmentAssets?: SegmentAsset[] | null
  sleeves?: Sleeve[] | null
  statusCodeNavigation?: AssetStatus
  structuresNearAssets?: StructuresNearAsset[] | null
  valves?: Valve[] | null
}

export type AssetRead = {
  id?: number
  continuousRouteId?: number
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  statusCode?: string | null
  assetCode?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  assetCalculationGroups?: AssetCalculationGroupRead[] | null
  assetCodeNavigation?: AssetTypeRead
  bends?: BendRead[] | null
  continuousRoute?: ContinuousRoute
  couplings?: CouplingRead[] | null
  flanges?: FlangeRead[] | null
  pipes?: Pipe[] | null
  riskResultWis?: RiskResultWisRead[] | null
  riskResults?: RiskResultRead[] | null
  riskTotalByAssetWis?: RiskTotalByAssetWisRead[] | null
  riskTotalByAssets?: RiskTotalByAssetRead[] | null
  riskTotalByFailureScenarioWis?: RiskTotalByFailureScenarioWisRead[] | null
  riskTotalByFailureScenarios?: RiskTotalByFailureScenarioRead[] | null
  riskTotalByImpactWis?: RiskTotalByImpactWisRead[] | null
  riskTotalByImpacts?: RiskTotalByImpactRead[] | null
  riskTotalByThreatWis?: RiskTotalByThreatWisRead[] | null
  riskTotalByThreats?: RiskTotalByThreatRead[] | null
  riskTotalDensityByImpactWis?: RiskTotalDensityByImpactWisRead[] | null
  riskTotalDensityByImpacts?: RiskTotalDensityByImpactRead[] | null
  riskTotalDensityByThreatWis?: RiskTotalDensityByThreatWisRead[] | null
  riskTotalDensityByThreats?: RiskTotalDensityByThreatRead[] | null
  segmentAssets?: SegmentAssetRead[] | null
  sleeves?: SleeveRead[] | null
  statusCodeNavigation?: AssetStatusRead
  structuresNearAssets?: StructuresNearAssetRead[] | null
  valves?: ValveRead[] | null
}

export type BackfillType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type BackfillTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type ClassLocationType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type ClassLocationTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type CoatingConditionType = {
  code?: string | null
  name?: string | null
  assessmentPipeCoatingConditionCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeWeldCoatingConditionCodeNavigations?: AssessmentPipe[] | null
  pipeCoatingConditionCodeNavigations?: Pipe[] | null
  pipeWeldCoatingConditionCodeNavigations?: Pipe[] | null
}

export type CoatingConditionTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipeCoatingConditionCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeWeldCoatingConditionCodeNavigations?: AssessmentPipe[] | null
  pipeCoatingConditionCodeNavigations?: Pipe[] | null
  pipeWeldCoatingConditionCodeNavigations?: Pipe[] | null
}

export type CoatingType = {
  code?: string | null
  name?: string | null
  assessmentPipeCoatingTypeCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeWeldCoatingTypeCodeNavigations?: AssessmentPipe[] | null
  pipeCoatingTypeCodeNavigations?: Pipe[] | null
  pipeWeldCoatingTypeCodeNavigations?: Pipe[] | null
}

export type CoatingTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipeCoatingTypeCodeNavigations?: AssessmentPipe[] | null
  assessmentPipeWeldCoatingTypeCodeNavigations?: AssessmentPipe[] | null
  pipeCoatingTypeCodeNavigations?: Pipe[] | null
  pipeWeldCoatingTypeCodeNavigations?: Pipe[] | null
}

export type CommodityType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type CommodityTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type CountryType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type CountryTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type FlowDirectionType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type FlowDirectionTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type GirthWeldType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type GirthWeldTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type ManufacturerType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type ManufacturerTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type MaterialType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type MaterialTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallExposureProcedureType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallExposureProcedureTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallLocateMethodType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallLocateMethodTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallNotificationRequirementType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallNotificationRequirementTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallRegistrationType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type OneCallRegistrationTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type SeamWeldType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type SeamWeldTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type SoilType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type SoilTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type WaterCrossingType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type WaterCrossingTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type Pipe = {
  id?: number
  assetId?: number
  createdBy?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  modifiedBy?: string | null
  aboveGroundFlag?: boolean | null
  acInducedCorrosionFlag?: boolean | null
  acIntrfrncFlag?: boolean | null
  additionalLoadAppliedFlag?: boolean | null
  algnmntMrkrInstalledFlag?: boolean | null
  algnmntMrkrSpcngStrtgyCode?: string | null
  anchorInstalledFlag?: boolean | null
  backfillCode?: string | null
  biocidesUsedFlag?: boolean | null
  blastingSusceptibility?: number | null
  bottomDentDiDate?: string | null
  bottomDentDiFndgCode?: string | null
  bottomDentDiRprFlag?: boolean | null
  bottomDentIliDate?: string | null
  bottomDentIliFndgCode?: string | null
  bottomDentIliRprFlag?: boolean | null
  bottomGougeDiDate?: string | null
  bottomGougeDiFndgCode?: string | null
  bottomGougeDiRprFlag?: boolean | null
  bottomGougeIliDate?: string | null
  bottomGougeIliFndgCode?: string | null
  bottomGougeIliRprFlag?: boolean | null
  bottomGougedDentDiDate?: string | null
  bottomGougedDentDiFndgCode?: string | null
  bottomGougedDentDiRprFlag?: boolean | null
  bottomGougedDentIliDate?: string | null
  bottomGougedDentIliFndgCode?: string | null
  bottomGougedDentIliRprFlag?: boolean | null
  bufferingAgentsUsedFlag?: boolean | null
  carbonDioxideMoleFraction?: number | null
  casingInstalledFlag?: boolean | null
  casingShortedFlag?: boolean | null
  charpyVNotchToughness?: number | null
  classLocationCode?: string | null
  cleaningPigUsedFlag?: boolean | null
  coatingApplyDate?: string | null
  coatingConditionCode?: string | null
  coatingDisbondment?: number | null
  coatingInspectionDate?: string | null
  coatingTypeCode?: string | null
  corrosionBeyondLowPointsFlag?: boolean | null
  corrosionCouponInstalledFlag?: boolean | null
  corrosionInhibitorsUsedFlag?: boolean | null
  cpSystemPerformance?: number | null
  dcIntrfrncFlag?: boolean | null
  depthOfCover?: number | null
  depthOfFrostLine?: number | null
  dripInstalledFlag?: boolean | null
  earthMvmntSusceptibility?: number | null
  elevationLowPointFlag?: boolean | null
  excavationFrequency?: number | null
  externalCorrosionDiFndgCode?: string | null
  externalCorrosionIliFndgCode?: string | null
  externalCorrosionRate?: number | null
  externalMicFlag?: boolean | null
  fieldBendAdjacentFlag?: boolean | null
  floodSusceptibility?: number | null
  foreignStructureIntrfrncFlag?: boolean | null
  girthWeldDefectCiFndgCode?: string | null
  girthWeldDefectDiFndgCode?: string | null
  girthWeldDefectIliFndgCode?: string | null
  girthWeldTypeCode?: string | null
  hardSpotDiFndgCode?: string | null
  hardSpotIliFndgCode?: string | null
  hydrogenSulfideMoleFraction?: number | null
  iliRunCmflFlag?: boolean | null
  iliRunEmatFlag?: boolean | null
  iliRunMflFlag?: boolean | null
  iliRunSccComboFlag?: boolean | null
  installationDate?: string | null
  internalCoatingFlag?: boolean | null
  internalCorrosionRate?: number | null
  internalMicFlag?: boolean | null
  landUseCode?: string | null
  landslideFrequency?: number | null
  lightningStrikeDensity?: number | null
  lineExhibitsSccFlag?: boolean | null
  manufactureDate?: string | null
  manufacturerCode?: string | null
  navigableWaterCrossingFlag?: boolean | null
  oneCallExposureProcdrCode?: string | null
  oneCallLocateMethodCode?: string | null
  oneCallNotificatnRqmntCode?: string | null
  oneCallRegistrationCode?: string | null
  oneCallResponseTime?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  oxygenMoleFraction?: number | null
  patrolInterval?: number | null
  pipeExposureFlag?: boolean | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  reassessmentInterval?: number | null
  roadCrossingFlag?: boolean | null
  sccAnomalyDiFndgCode?: string | null
  sccAnomalyIliFndgCode?: string | null
  seamWeldDefectDiFndgCode?: string | null
  seamWeldDefectIliFndgCode?: string | null
  seamWeldTypeCode?: string | null
  seismicEventFrequency?: number | null
  sleeveInstalledFlag?: boolean | null
  soilResistivity?: number | null
  slopeStabilizedFlag?: boolean | null
  soilTypeCode?: string | null
  ssi?: number | null
  sswcDiFndgCode?: string | null
  sswcIliFndgCode?: string | null
  subsidenceSusceptibility?: number | null
  tensileStrength?: number | null
  testDate?: string | null
  topDentDiFndgCode?: string | null
  topDentIliFndgCode?: string | null
  topGougeDiFndgCode?: string | null
  topGougeIliFndgCode?: string | null
  topGougedDentDiFndgCode?: string | null
  topGougedDentIliFndgCode?: string | null
  upstreamCompressorFlag?: boolean | null
  wallThickness?: number | null
  waterMoleFraction?: number | null
  weightInstalledFlag?: boolean | null
  weldCoatingApplyDate?: string | null
  weldCoatingConditionCode?: string | null
  weldCoatingDisbondment?: number | null
  weldCoatingExtent?: number | null
  weldCoatingInspectionDate?: string | null
  weldCoatingTypeCode?: string | null
  yieldStrength?: number | null
  youngsModulus?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  consequenceAreaCode?: string | null
  avgDepthOfCoverIn?: number | null
  beginValveId?: number | null
  commodityCode?: string | null
  countryCode?: string | null
  cpCriteriaMetFlag?: boolean | null
  ecdaDate?: string | null
  electricalIntrfrncFlag?: boolean | null
  electricalShieldingFlag?: boolean | null
  endValveId?: number | null
  flowDirectionCode?: string | null
  foreignLineCrossingFlag?: boolean | null
  icdaDate?: string | null
  interstatePipeFlag?: boolean | null
  isolationSectionLength?: number | null
  isolationSectionRangeName?: string | null
  maop?: number | null
  materialCode?: string | null
  maxDepthOfCoverIn?: number | null
  nominalPipeSize?: number | null
  offShorePipeFlag?: boolean | null
  operatingStress?: number | null
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  pipelineCode?: string | null
  segmentLength?: number | null
  smts?: number | null
  smys?: number | null
  utilityRowCrossingFlag?: boolean | null
  waterCrossingTypeCode?: string | null
  algnmntMrkrSpcngStrtgyCodeNavigation?: AlignmentMarkerSpacingStrategyType
  asset?: Asset
  backfillCodeNavigation?: BackfillType
  bottomDentDiFndgCodeNavigation?: InspectionFindingType
  bottomDentIliFndgCodeNavigation?: InspectionFindingType
  bottomGougeDiFndgCodeNavigation?: InspectionFindingType
  bottomGougeIliFndgCodeNavigation?: InspectionFindingType
  bottomGougedDentDiFndgCodeNavigation?: InspectionFindingType
  bottomGougedDentIliFndgCodeNavigation?: InspectionFindingType
  classLocationCodeNavigation?: ClassLocationType
  coatingConditionCodeNavigation?: CoatingConditionType
  coatingTypeCodeNavigation?: CoatingType
  commodityCodeNavigation?: CommodityType
  consequenceAreaCodeNavigation?: ConsequenceAreaType
  countryCodeNavigation?: CountryType
  externalCorrosionDiFndgCodeNavigation?: InspectionFindingType
  externalCorrosionIliFndgCodeNavigation?: InspectionFindingType
  flowDirectionCodeNavigation?: FlowDirectionType
  girthWeldDefectCiFndgCodeNavigation?: InspectionFindingType
  girthWeldDefectDiFndgCodeNavigation?: InspectionFindingType
  girthWeldDefectIliFndgCodeNavigation?: InspectionFindingType
  girthWeldTypeCodeNavigation?: GirthWeldType
  hardSpotDiFndgCodeNavigation?: InspectionFindingType
  hardSpotIliFndgCodeNavigation?: InspectionFindingType
  landUseCodeNavigation?: LandUseType
  manufacturerCodeNavigation?: ManufacturerType
  materialCodeNavigation?: MaterialType
  oneCallExposureProcdrCodeNavigation?: OneCallExposureProcedureType
  oneCallLocateMethodCodeNavigation?: OneCallLocateMethodType
  oneCallNotificatnRqmntCodeNavigation?: OneCallNotificationRequirementType
  oneCallRegistrationCodeNavigation?: OneCallRegistrationType
  operatorCodeNavigation?: OperatorType
  regionCodeNavigation?: RegionType
  sccAnomalyDiFndgCodeNavigation?: InspectionFindingType
  sccAnomalyIliFndgCodeNavigation?: InspectionFindingType
  seamWeldDefectDiFndgCodeNavigation?: InspectionFindingType
  seamWeldDefectIliFndgCodeNavigation?: InspectionFindingType
  seamWeldTypeCodeNavigation?: SeamWeldType
  soilTypeCodeNavigation?: SoilType
  sswcDiFndgCodeNavigation?: InspectionFindingType
  sswcIliFndgCodeNavigation?: InspectionFindingType
  stateCodeNavigation?: StateType
  topDentDiFndgCodeNavigation?: InspectionFindingType
  topDentIliFndgCodeNavigation?: InspectionFindingType
  topGougeDiFndgCodeNavigation?: InspectionFindingType
  topGougeIliFndgCodeNavigation?: InspectionFindingType
  topGougedDentDiFndgCodeNavigation?: InspectionFindingType
  topGougedDentIliFndgCodeNavigation?: InspectionFindingType
  waterCrossingTypeCodeNavigation?: WaterCrossingType
  weldCoatingConditionCodeNavigation?: CoatingConditionType
  weldCoatingTypeCodeNavigation?: CoatingType
}

export type PipeRead = {
  id?: number
  assetId?: number
  createdBy?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  modifiedBy?: string | null
  aboveGroundFlag?: boolean | null
  acInducedCorrosionFlag?: boolean | null
  acIntrfrncFlag?: boolean | null
  additionalLoadAppliedFlag?: boolean | null
  algnmntMrkrInstalledFlag?: boolean | null
  algnmntMrkrSpcngStrtgyCode?: string | null
  anchorInstalledFlag?: boolean | null
  backfillCode?: string | null
  biocidesUsedFlag?: boolean | null
  blastingSusceptibility?: number | null
  bottomDentDiDate?: string | null
  bottomDentDiFndgCode?: string | null
  bottomDentDiRprFlag?: boolean | null
  bottomDentIliDate?: string | null
  bottomDentIliFndgCode?: string | null
  bottomDentIliRprFlag?: boolean | null
  bottomGougeDiDate?: string | null
  bottomGougeDiFndgCode?: string | null
  bottomGougeDiRprFlag?: boolean | null
  bottomGougeIliDate?: string | null
  bottomGougeIliFndgCode?: string | null
  bottomGougeIliRprFlag?: boolean | null
  bottomGougedDentDiDate?: string | null
  bottomGougedDentDiFndgCode?: string | null
  bottomGougedDentDiRprFlag?: boolean | null
  bottomGougedDentIliDate?: string | null
  bottomGougedDentIliFndgCode?: string | null
  bottomGougedDentIliRprFlag?: boolean | null
  bufferingAgentsUsedFlag?: boolean | null
  carbonDioxideMoleFraction?: number | null
  casingInstalledFlag?: boolean | null
  casingShortedFlag?: boolean | null
  charpyVNotchToughness?: number | null
  classLocationCode?: string | null
  cleaningPigUsedFlag?: boolean | null
  coatingApplyDate?: string | null
  coatingConditionCode?: string | null
  coatingDisbondment?: number | null
  coatingInspectionDate?: string | null
  coatingTypeCode?: string | null
  corrosionBeyondLowPointsFlag?: boolean | null
  corrosionCouponInstalledFlag?: boolean | null
  corrosionInhibitorsUsedFlag?: boolean | null
  cpSystemPerformance?: number | null
  dcIntrfrncFlag?: boolean | null
  depthOfCover?: number | null
  depthOfFrostLine?: number | null
  dripInstalledFlag?: boolean | null
  earthMvmntSusceptibility?: number | null
  elevationLowPointFlag?: boolean | null
  excavationFrequency?: number | null
  externalCorrosionDiFndgCode?: string | null
  externalCorrosionIliFndgCode?: string | null
  externalCorrosionRate?: number | null
  externalMicFlag?: boolean | null
  fieldBendAdjacentFlag?: boolean | null
  floodSusceptibility?: number | null
  foreignStructureIntrfrncFlag?: boolean | null
  girthWeldDefectCiFndgCode?: string | null
  girthWeldDefectDiFndgCode?: string | null
  girthWeldDefectIliFndgCode?: string | null
  girthWeldTypeCode?: string | null
  hardSpotDiFndgCode?: string | null
  hardSpotIliFndgCode?: string | null
  hydrogenSulfideMoleFraction?: number | null
  iliRunCmflFlag?: boolean | null
  iliRunEmatFlag?: boolean | null
  iliRunMflFlag?: boolean | null
  iliRunSccComboFlag?: boolean | null
  installationDate?: string | null
  internalCoatingFlag?: boolean | null
  internalCorrosionRate?: number | null
  internalMicFlag?: boolean | null
  landUseCode?: string | null
  landslideFrequency?: number | null
  lightningStrikeDensity?: number | null
  lineExhibitsSccFlag?: boolean | null
  manufactureDate?: string | null
  manufacturerCode?: string | null
  navigableWaterCrossingFlag?: boolean | null
  oneCallExposureProcdrCode?: string | null
  oneCallLocateMethodCode?: string | null
  oneCallNotificatnRqmntCode?: string | null
  oneCallRegistrationCode?: string | null
  oneCallResponseTime?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  oxygenMoleFraction?: number | null
  patrolInterval?: number | null
  pipeExposureFlag?: boolean | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  reassessmentInterval?: number | null
  roadCrossingFlag?: boolean | null
  sccAnomalyDiFndgCode?: string | null
  sccAnomalyIliFndgCode?: string | null
  seamWeldDefectDiFndgCode?: string | null
  seamWeldDefectIliFndgCode?: string | null
  seamWeldTypeCode?: string | null
  seismicEventFrequency?: number | null
  sleeveInstalledFlag?: boolean | null
  soilResistivity?: number | null
  slopeStabilizedFlag?: boolean | null
  soilTypeCode?: string | null
  ssi?: number | null
  sswcDiFndgCode?: string | null
  sswcIliFndgCode?: string | null
  subsidenceSusceptibility?: number | null
  tensileStrength?: number | null
  testDate?: string | null
  topDentDiFndgCode?: string | null
  topDentIliFndgCode?: string | null
  topGougeDiFndgCode?: string | null
  topGougeIliFndgCode?: string | null
  topGougedDentDiFndgCode?: string | null
  topGougedDentIliFndgCode?: string | null
  upstreamCompressorFlag?: boolean | null
  wallThickness?: number | null
  waterMoleFraction?: number | null
  weightInstalledFlag?: boolean | null
  weldCoatingApplyDate?: string | null
  weldCoatingConditionCode?: string | null
  weldCoatingDisbondment?: number | null
  weldCoatingExtent?: number | null
  weldCoatingInspectionDate?: string | null
  weldCoatingTypeCode?: string | null
  yieldStrength?: number | null
  youngsModulus?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  consequenceAreaCode?: string | null
  avgDepthOfCoverIn?: number | null
  beginValveId?: number | null
  commodityCode?: string | null
  countryCode?: string | null
  cpCriteriaMetFlag?: boolean | null
  ecdaDate?: string | null
  electricalIntrfrncFlag?: boolean | null
  electricalShieldingFlag?: boolean | null
  endValveId?: number | null
  flowDirectionCode?: string | null
  foreignLineCrossingFlag?: boolean | null
  icdaDate?: string | null
  interstatePipeFlag?: boolean | null
  isolationSectionLength?: number | null
  isolationSectionRangeName?: string | null
  maop?: number | null
  materialCode?: string | null
  maxDepthOfCoverIn?: number | null
  nominalPipeSize?: number | null
  offShorePipeFlag?: boolean | null
  operatingStress?: number | null
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  pipelineCode?: string | null
  segmentLength?: number | null
  smts?: number | null
  smys?: number | null
  utilityRowCrossingFlag?: boolean | null
  waterCrossingTypeCode?: string | null
  algnmntMrkrSpcngStrtgyCodeNavigation?: AlignmentMarkerSpacingStrategyType
  asset?: AssetRead
  backfillCodeNavigation?: BackfillTypeRead
  bottomDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougeDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougeIliFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougedDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougedDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  classLocationCodeNavigation?: ClassLocationTypeRead
  coatingConditionCodeNavigation?: CoatingConditionTypeRead
  coatingTypeCodeNavigation?: CoatingTypeRead
  commodityCodeNavigation?: CommodityTypeRead
  consequenceAreaCodeNavigation?: ConsequenceAreaType
  countryCodeNavigation?: CountryTypeRead
  externalCorrosionDiFndgCodeNavigation?: InspectionFindingTypeRead
  externalCorrosionIliFndgCodeNavigation?: InspectionFindingTypeRead
  flowDirectionCodeNavigation?: FlowDirectionTypeRead
  girthWeldDefectCiFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldDefectDiFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldDefectIliFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldTypeCodeNavigation?: GirthWeldTypeRead
  hardSpotDiFndgCodeNavigation?: InspectionFindingTypeRead
  hardSpotIliFndgCodeNavigation?: InspectionFindingTypeRead
  landUseCodeNavigation?: LandUseTypeRead
  manufacturerCodeNavigation?: ManufacturerTypeRead
  materialCodeNavigation?: MaterialTypeRead
  oneCallExposureProcdrCodeNavigation?: OneCallExposureProcedureTypeRead
  oneCallLocateMethodCodeNavigation?: OneCallLocateMethodTypeRead
  oneCallNotificatnRqmntCodeNavigation?: OneCallNotificationRequirementTypeRead
  oneCallRegistrationCodeNavigation?: OneCallRegistrationTypeRead
  operatorCodeNavigation?: OperatorTypeRead
  regionCodeNavigation?: RegionTypeRead
  sccAnomalyDiFndgCodeNavigation?: InspectionFindingTypeRead
  sccAnomalyIliFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldDefectDiFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldDefectIliFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldTypeCodeNavigation?: SeamWeldTypeRead
  soilTypeCodeNavigation?: SoilTypeRead
  sswcDiFndgCodeNavigation?: InspectionFindingTypeRead
  sswcIliFndgCodeNavigation?: InspectionFindingTypeRead
  stateCodeNavigation?: StateTypeRead
  topDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  topDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  topGougeDiFndgCodeNavigation?: InspectionFindingTypeRead
  topGougeIliFndgCodeNavigation?: InspectionFindingTypeRead
  topGougedDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  topGougedDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  waterCrossingTypeCodeNavigation?: WaterCrossingTypeRead
  weldCoatingConditionCodeNavigation?: CoatingConditionTypeRead
  weldCoatingTypeCodeNavigation?: CoatingTypeRead
}

export type AlignmentMarkerSpacingStrategyType = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type AlignmentMarkerSpacingStrategyTypeRead = {
  code?: string | null
  name?: string | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: PipeRead[] | null
}

export type AssessmentPipe = {
  id?: number
  assessmentId?: number
  assetId?: number
  createdBy?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  modifiedBy?: string | null
  aboveGroundFlag?: boolean | null
  acInducedCorrosionFlag?: boolean | null
  acIntrfrncFlag?: boolean | null
  additionalLoadAppliedFlag?: boolean | null
  algnmntMrkrInstalledFlag?: boolean | null
  algnmntMrkrSpcngStrtgyCode?: string | null
  anchorInstalledFlag?: boolean | null
  backfillCode?: string | null
  biocidesUsedFlag?: boolean | null
  blastingSusceptibility?: number | null
  bottomDentDiDate?: string | null
  bottomDentDiFndgCode?: string | null
  bottomDentDiRprFlag?: boolean | null
  bottomDentIliDate?: string | null
  bottomDentIliFndgCode?: string | null
  bottomDentIliRprFlag?: boolean | null
  bottomGougeDiDate?: string | null
  bottomGougeDiFndgCode?: string | null
  bottomGougeDiRprFlag?: boolean | null
  bottomGougeIliDate?: string | null
  bottomGougeIliFndgCode?: string | null
  bottomGougeIliRprFlag?: boolean | null
  bottomGougedDentDiDate?: string | null
  bottomGougedDentDiFndgCode?: string | null
  bottomGougedDentDiRprFlag?: boolean | null
  bottomGougedDentIliDate?: string | null
  bottomGougedDentIliFndgCode?: string | null
  bottomGougedDentIliRprFlag?: boolean | null
  bufferingAgentsUsedFlag?: boolean | null
  carbonDioxideMoleFraction?: number | null
  casingInstalledFlag?: boolean | null
  casingShortedFlag?: boolean | null
  charpyVNotchToughness?: number | null
  classLocationCode?: string | null
  cleaningPigUsedFlag?: boolean | null
  coatingApplyDate?: string | null
  coatingConditionCode?: string | null
  coatingDisbondment?: number | null
  coatingInspectionDate?: string | null
  coatingTypeCode?: string | null
  corrosionBeyondLowPointsFlag?: boolean | null
  corrosionCouponInstalledFlag?: boolean | null
  corrosionInhibitorsUsedFlag?: boolean | null
  cpSystemPerformance?: number | null
  dcIntrfrncFlag?: boolean | null
  depthOfCover?: number | null
  depthOfFrostLine?: number | null
  dripInstalledFlag?: boolean | null
  earthMvmntSusceptibility?: number | null
  elevationLowPointFlag?: boolean | null
  excavationFrequency?: number | null
  externalCorrosionDiFndgCode?: string | null
  externalCorrosionIliFndgCode?: string | null
  externalCorrosionRate?: number | null
  externalMicFlag?: boolean | null
  fieldBendAdjacentFlag?: boolean | null
  floodSusceptibility?: number | null
  foreignStructureIntrfrncFlag?: boolean | null
  girthWeldDefectCiFndgCode?: string | null
  girthWeldDefectDiFndgCode?: string | null
  girthWeldDefectIliFndgCode?: string | null
  girthWeldTypeCode?: string | null
  hardSpotDiFndgCode?: string | null
  hardSpotIliFndgCode?: string | null
  hydrogenSulfideMoleFraction?: number | null
  iliRunCmflFlag?: boolean | null
  iliRunEmatFlag?: boolean | null
  iliRunMflFlag?: boolean | null
  iliRunSccComboFlag?: boolean | null
  installationDate?: string | null
  internalCoatingFlag?: boolean | null
  internalCorrosionRate?: number | null
  internalMicFlag?: boolean | null
  landUseCode?: string | null
  landslideFrequency?: number | null
  lightningStrikeDensity?: number | null
  lineExhibitsSccFlag?: boolean | null
  manufactureDate?: string | null
  manufacturerCode?: string | null
  navigableWaterCrossingFlag?: boolean | null
  oneCallExposureProcdrCode?: string | null
  oneCallLocateMethodCode?: string | null
  oneCallNotificatnRqmntCode?: string | null
  oneCallRegistrationCode?: string | null
  oneCallResponseTime?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  oxygenMoleFraction?: number | null
  patrolInterval?: number | null
  pipeExposureFlag?: boolean | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  reassessmentInterval?: number | null
  roadCrossingFlag?: boolean | null
  sccAnomalyDiFndgCode?: string | null
  sccAnomalyIliFndgCode?: string | null
  seamWeldDefectDiFndgCode?: string | null
  seamWeldDefectIliFndgCode?: string | null
  seamWeldTypeCode?: string | null
  seismicEventFrequency?: number | null
  sleeveInstalledFlag?: boolean | null
  soilResistivity?: number | null
  slopeStabilizedFlag?: boolean | null
  soilTypeCode?: string | null
  ssi?: number | null
  sswcDiFndgCode?: string | null
  sswcIliFndgCode?: string | null
  subsidenceSusceptibility?: number | null
  tensileStrength?: number | null
  testDate?: string | null
  topDentDiFndgCode?: string | null
  topDentIliFndgCode?: string | null
  topGougeDiFndgCode?: string | null
  topGougeIliFndgCode?: string | null
  topGougedDentDiFndgCode?: string | null
  topGougedDentIliFndgCode?: string | null
  upstreamCompressorFlag?: boolean | null
  wallThickness?: number | null
  waterMoleFraction?: number | null
  weightInstalledFlag?: boolean | null
  weldCoatingApplyDate?: string | null
  weldCoatingConditionCode?: string | null
  weldCoatingDisbondment?: number | null
  weldCoatingExtent?: number | null
  weldCoatingInspectionDate?: string | null
  weldCoatingTypeCode?: string | null
  yieldStrength?: number | null
  youngsModulus?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  consequenceAreaCode?: string | null
  avgDepthOfCoverIn?: number | null
  beginValveId?: number | null
  commodityCode?: string | null
  countryCode?: string | null
  cpCriteriaMetFlag?: boolean | null
  ecdaDate?: string | null
  electricalIntrfrncFlag?: boolean | null
  electricalShieldingFlag?: boolean | null
  endValveId?: number | null
  flowDirectionCode?: string | null
  foreignLineCrossingFlag?: boolean | null
  icdaDate?: string | null
  interstatePipeFlag?: boolean | null
  isolationSectionLength?: number | null
  isolationSectionRangeName?: string | null
  maop?: number | null
  materialCode?: string | null
  maxDepthOfCoverIn?: number | null
  nominalPipeSize?: number | null
  offShorePipeFlag?: boolean | null
  operatingStress?: number | null
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  pipelineCode?: string | null
  segmentLength?: number | null
  smts?: number | null
  smys?: number | null
  utilityRowCrossingFlag?: boolean | null
  waterCrossingTypeCode?: string | null
  algnmntMrkrSpcngStrtgyCodeNavigation?: AlignmentMarkerSpacingStrategyType
  asset?: Asset
  backfillCodeNavigation?: BackfillType
  bottomDentDiFndgCodeNavigation?: InspectionFindingType
  bottomDentIliFndgCodeNavigation?: InspectionFindingType
  bottomGougeDiFndgCodeNavigation?: InspectionFindingType
  bottomGougeIliFndgCodeNavigation?: InspectionFindingType
  bottomGougedDentDiFndgCodeNavigation?: InspectionFindingType
  bottomGougedDentIliFndgCodeNavigation?: InspectionFindingType
  classLocationCodeNavigation?: ClassLocationType
  coatingConditionCodeNavigation?: CoatingConditionType
  coatingTypeCodeNavigation?: CoatingType
  commodityCodeNavigation?: CommodityType
  consequenceAreaCodeNavigation?: ConsequenceAreaType
  countryCodeNavigation?: CountryType
  externalCorrosionDiFndgCodeNavigation?: InspectionFindingType
  externalCorrosionIliFndgCodeNavigation?: InspectionFindingType
  flowDirectionCodeNavigation?: FlowDirectionType
  girthWeldDefectCiFndgCodeNavigation?: InspectionFindingType
  girthWeldDefectDiFndgCodeNavigation?: InspectionFindingType
  girthWeldDefectIliFndgCodeNavigation?: InspectionFindingType
  girthWeldTypeCodeNavigation?: GirthWeldType
  hardSpotDiFndgCodeNavigation?: InspectionFindingType
  hardSpotIliFndgCodeNavigation?: InspectionFindingType
  landUseCodeNavigation?: LandUseType
  manufacturerCodeNavigation?: ManufacturerType
  materialCodeNavigation?: MaterialType
  oneCallExposureProcdrCodeNavigation?: OneCallExposureProcedureType
  oneCallLocateMethodCodeNavigation?: OneCallLocateMethodType
  oneCallNotificatnRqmntCodeNavigation?: OneCallNotificationRequirementType
  oneCallRegistrationCodeNavigation?: OneCallRegistrationType
  operatorCodeNavigation?: OperatorType
  regionCodeNavigation?: RegionType
  sccAnomalyDiFndgCodeNavigation?: InspectionFindingType
  sccAnomalyIliFndgCodeNavigation?: InspectionFindingType
  seamWeldDefectDiFndgCodeNavigation?: InspectionFindingType
  seamWeldDefectIliFndgCodeNavigation?: InspectionFindingType
  seamWeldTypeCodeNavigation?: SeamWeldType
  soilTypeCodeNavigation?: SoilType
  sswcDiFndgCodeNavigation?: InspectionFindingType
  sswcIliFndgCodeNavigation?: InspectionFindingType
  stateCodeNavigation?: StateType
  topDentDiFndgCodeNavigation?: InspectionFindingType
  topDentIliFndgCodeNavigation?: InspectionFindingType
  topGougeDiFndgCodeNavigation?: InspectionFindingType
  topGougeIliFndgCodeNavigation?: InspectionFindingType
  topGougedDentDiFndgCodeNavigation?: InspectionFindingType
  topGougedDentIliFndgCodeNavigation?: InspectionFindingType
  waterCrossingTypeCodeNavigation?: WaterCrossingType
  weldCoatingConditionCodeNavigation?: CoatingConditionType
  weldCoatingTypeCodeNavigation?: CoatingType
}

export type AssessmentPipeRead = {
  id?: number
  assessmentId?: number
  assetId?: number
  createdBy?: string | null
  dateCreated?: string | null
  dateModified?: string | null
  modifiedBy?: string | null
  aboveGroundFlag?: boolean | null
  acInducedCorrosionFlag?: boolean | null
  acIntrfrncFlag?: boolean | null
  additionalLoadAppliedFlag?: boolean | null
  algnmntMrkrInstalledFlag?: boolean | null
  algnmntMrkrSpcngStrtgyCode?: string | null
  anchorInstalledFlag?: boolean | null
  backfillCode?: string | null
  biocidesUsedFlag?: boolean | null
  blastingSusceptibility?: number | null
  bottomDentDiDate?: string | null
  bottomDentDiFndgCode?: string | null
  bottomDentDiRprFlag?: boolean | null
  bottomDentIliDate?: string | null
  bottomDentIliFndgCode?: string | null
  bottomDentIliRprFlag?: boolean | null
  bottomGougeDiDate?: string | null
  bottomGougeDiFndgCode?: string | null
  bottomGougeDiRprFlag?: boolean | null
  bottomGougeIliDate?: string | null
  bottomGougeIliFndgCode?: string | null
  bottomGougeIliRprFlag?: boolean | null
  bottomGougedDentDiDate?: string | null
  bottomGougedDentDiFndgCode?: string | null
  bottomGougedDentDiRprFlag?: boolean | null
  bottomGougedDentIliDate?: string | null
  bottomGougedDentIliFndgCode?: string | null
  bottomGougedDentIliRprFlag?: boolean | null
  bufferingAgentsUsedFlag?: boolean | null
  carbonDioxideMoleFraction?: number | null
  casingInstalledFlag?: boolean | null
  casingShortedFlag?: boolean | null
  charpyVNotchToughness?: number | null
  classLocationCode?: string | null
  cleaningPigUsedFlag?: boolean | null
  coatingApplyDate?: string | null
  coatingConditionCode?: string | null
  coatingDisbondment?: number | null
  coatingInspectionDate?: string | null
  coatingTypeCode?: string | null
  corrosionBeyondLowPointsFlag?: boolean | null
  corrosionCouponInstalledFlag?: boolean | null
  corrosionInhibitorsUsedFlag?: boolean | null
  cpSystemPerformance?: number | null
  dcIntrfrncFlag?: boolean | null
  depthOfCover?: number | null
  depthOfFrostLine?: number | null
  dripInstalledFlag?: boolean | null
  earthMvmntSusceptibility?: number | null
  elevationLowPointFlag?: boolean | null
  excavationFrequency?: number | null
  externalCorrosionDiFndgCode?: string | null
  externalCorrosionIliFndgCode?: string | null
  externalCorrosionRate?: number | null
  externalMicFlag?: boolean | null
  fieldBendAdjacentFlag?: boolean | null
  floodSusceptibility?: number | null
  foreignStructureIntrfrncFlag?: boolean | null
  girthWeldDefectCiFndgCode?: string | null
  girthWeldDefectDiFndgCode?: string | null
  girthWeldDefectIliFndgCode?: string | null
  girthWeldTypeCode?: string | null
  hardSpotDiFndgCode?: string | null
  hardSpotIliFndgCode?: string | null
  hydrogenSulfideMoleFraction?: number | null
  iliRunCmflFlag?: boolean | null
  iliRunEmatFlag?: boolean | null
  iliRunMflFlag?: boolean | null
  iliRunSccComboFlag?: boolean | null
  installationDate?: string | null
  internalCoatingFlag?: boolean | null
  internalCorrosionRate?: number | null
  internalMicFlag?: boolean | null
  landUseCode?: string | null
  landslideFrequency?: number | null
  lightningStrikeDensity?: number | null
  lineExhibitsSccFlag?: boolean | null
  manufactureDate?: string | null
  manufacturerCode?: string | null
  navigableWaterCrossingFlag?: boolean | null
  oneCallExposureProcdrCode?: string | null
  oneCallLocateMethodCode?: string | null
  oneCallNotificatnRqmntCode?: string | null
  oneCallRegistrationCode?: string | null
  oneCallResponseTime?: number | null
  operatingPressure?: number | null
  operatingTemperature?: number | null
  outsideDiameter?: number | null
  oxygenMoleFraction?: number | null
  patrolInterval?: number | null
  pipeExposureFlag?: boolean | null
  protectBarrierInstallFlag?: boolean | null
  railCrossingFlag?: boolean | null
  reassessmentInterval?: number | null
  roadCrossingFlag?: boolean | null
  sccAnomalyDiFndgCode?: string | null
  sccAnomalyIliFndgCode?: string | null
  seamWeldDefectDiFndgCode?: string | null
  seamWeldDefectIliFndgCode?: string | null
  seamWeldTypeCode?: string | null
  seismicEventFrequency?: number | null
  sleeveInstalledFlag?: boolean | null
  soilResistivity?: number | null
  slopeStabilizedFlag?: boolean | null
  soilTypeCode?: string | null
  ssi?: number | null
  sswcDiFndgCode?: string | null
  sswcIliFndgCode?: string | null
  subsidenceSusceptibility?: number | null
  tensileStrength?: number | null
  testDate?: string | null
  topDentDiFndgCode?: string | null
  topDentIliFndgCode?: string | null
  topGougeDiFndgCode?: string | null
  topGougeIliFndgCode?: string | null
  topGougedDentDiFndgCode?: string | null
  topGougedDentIliFndgCode?: string | null
  upstreamCompressorFlag?: boolean | null
  wallThickness?: number | null
  waterMoleFraction?: number | null
  weightInstalledFlag?: boolean | null
  weldCoatingApplyDate?: string | null
  weldCoatingConditionCode?: string | null
  weldCoatingDisbondment?: number | null
  weldCoatingExtent?: number | null
  weldCoatingInspectionDate?: string | null
  weldCoatingTypeCode?: string | null
  yieldStrength?: number | null
  youngsModulus?: number | null
  dependentCustomerCount?: number | null
  isolationSectionVolume?: number | null
  populatedAreaFlag?: boolean | null
  regionCode?: string | null
  stateCode?: string | null
  waterCrossingFlag?: boolean | null
  consequenceAreaCode?: string | null
  avgDepthOfCoverIn?: number | null
  beginValveId?: number | null
  commodityCode?: string | null
  countryCode?: string | null
  cpCriteriaMetFlag?: boolean | null
  ecdaDate?: string | null
  electricalIntrfrncFlag?: boolean | null
  electricalShieldingFlag?: boolean | null
  endValveId?: number | null
  flowDirectionCode?: string | null
  foreignLineCrossingFlag?: boolean | null
  icdaDate?: string | null
  interstatePipeFlag?: boolean | null
  isolationSectionLength?: number | null
  isolationSectionRangeName?: string | null
  maop?: number | null
  materialCode?: string | null
  maxDepthOfCoverIn?: number | null
  nominalPipeSize?: number | null
  offShorePipeFlag?: boolean | null
  operatingStress?: number | null
  operatorCode?: string | null
  phmsaOperatorId?: number | null
  pipelineCode?: string | null
  segmentLength?: number | null
  smts?: number | null
  smys?: number | null
  utilityRowCrossingFlag?: boolean | null
  waterCrossingTypeCode?: string | null
  algnmntMrkrSpcngStrtgyCodeNavigation?: AlignmentMarkerSpacingStrategyTypeRead
  asset?: AssetRead
  backfillCodeNavigation?: BackfillTypeRead
  bottomDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougeDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougeIliFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougedDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  bottomGougedDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  classLocationCodeNavigation?: ClassLocationTypeRead
  coatingConditionCodeNavigation?: CoatingConditionTypeRead
  coatingTypeCodeNavigation?: CoatingTypeRead
  commodityCodeNavigation?: CommodityTypeRead
  consequenceAreaCodeNavigation?: ConsequenceAreaType
  countryCodeNavigation?: CountryTypeRead
  externalCorrosionDiFndgCodeNavigation?: InspectionFindingTypeRead
  externalCorrosionIliFndgCodeNavigation?: InspectionFindingTypeRead
  flowDirectionCodeNavigation?: FlowDirectionTypeRead
  girthWeldDefectCiFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldDefectDiFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldDefectIliFndgCodeNavigation?: InspectionFindingTypeRead
  girthWeldTypeCodeNavigation?: GirthWeldTypeRead
  hardSpotDiFndgCodeNavigation?: InspectionFindingTypeRead
  hardSpotIliFndgCodeNavigation?: InspectionFindingTypeRead
  landUseCodeNavigation?: LandUseTypeRead
  manufacturerCodeNavigation?: ManufacturerTypeRead
  materialCodeNavigation?: MaterialTypeRead
  oneCallExposureProcdrCodeNavigation?: OneCallExposureProcedureTypeRead
  oneCallLocateMethodCodeNavigation?: OneCallLocateMethodTypeRead
  oneCallNotificatnRqmntCodeNavigation?: OneCallNotificationRequirementTypeRead
  oneCallRegistrationCodeNavigation?: OneCallRegistrationTypeRead
  operatorCodeNavigation?: OperatorTypeRead
  regionCodeNavigation?: RegionTypeRead
  sccAnomalyDiFndgCodeNavigation?: InspectionFindingTypeRead
  sccAnomalyIliFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldDefectDiFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldDefectIliFndgCodeNavigation?: InspectionFindingTypeRead
  seamWeldTypeCodeNavigation?: SeamWeldTypeRead
  soilTypeCodeNavigation?: SoilTypeRead
  sswcDiFndgCodeNavigation?: InspectionFindingTypeRead
  sswcIliFndgCodeNavigation?: InspectionFindingTypeRead
  stateCodeNavigation?: StateTypeRead
  topDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  topDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  topGougeDiFndgCodeNavigation?: InspectionFindingTypeRead
  topGougeIliFndgCodeNavigation?: InspectionFindingTypeRead
  topGougedDentDiFndgCodeNavigation?: InspectionFindingTypeRead
  topGougedDentIliFndgCodeNavigation?: InspectionFindingTypeRead
  waterCrossingTypeCodeNavigation?: WaterCrossingTypeRead
  weldCoatingConditionCodeNavigation?: CoatingConditionTypeRead
  weldCoatingTypeCodeNavigation?: CoatingTypeRead
}

export type ConsequenceAreaType = {
  code?: string | null
  name?: string | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevel[] | null
  assessmentPipes?: AssessmentPipe[] | null
  pipes?: Pipe[] | null
}

export type ConsequenceAreaTypeRead = {
  code?: string | null
  name?: string | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevel[] | null
  assessmentPipes?: AssessmentPipeRead[] | null
  pipes?: PipeRead[] | null
}

export type AssessmentConsequenceLevel = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  codeNavigation?: ConsequenceAreaType
}

export type AssessmentConsequenceLevelRead = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  codeNavigation?: ConsequenceAreaTypeRead
}

export type AcceptanceCategory = {
  id?: number
  minValue?: number
  maxValue?: number
  maxDensityValue?: number
  minDensityValue?: number
  code?: string | null
  name?: string | null
  colour?: string | null
  assessmentRiskLevels?: AssessmentRiskLevel[] | null
}

export type AssessmentRiskLevel = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  codeNavigation?: AcceptanceCategory
}

export type AssessmentRiskLevelRead = {
  id?: number
  assessmentId?: number
  code?: string | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  codeNavigation?: AcceptanceCategory
}

export type AssessmentScenario = {
  id?: number
  name?: string | null
  rules?: string | null
  dateCreated?: string
  dateModified?: string
  assessmentId?: number
  assessment?: Assessment
}

export type AssessmentScenarioRead = {
  id?: number
  name?: string | null
  rules?: string | null
  dateCreated?: string
  dateModified?: string
  assessmentId?: number
  assessment?: Assessment
}

export type RiskTotalBySegmentWis = {
  id?: number
  assessmentId?: number
  segmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
}

export type RiskTotalBySegmentWisRead = {
  id?: number
  assessmentId?: number
  segmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
}

export type RiskTotalBySegment = {
  id?: number
  segmentId?: number
  assessmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
}

export type RiskTotalBySegmentRead = {
  id?: number
  segmentId?: number
  assessmentId?: number
  riskDensity?: number | null
  riskTotal?: number | null
  forecastYear?: number
  assessment?: Assessment
}

export type AssessmentStatus = {
  code?: string | null
  description?: string | null
  assessments?: Assessment[] | null
}

export type AssessmentStatusRead = {
  code?: string | null
  description?: string | null
  assessments?: Assessment[] | null
}

export type AssessmentType = {
  code?: string | null
  name?: string | null
  assessments?: Assessment[] | null
}

export type AssessmentTypeRead = {
  code?: string | null
  name?: string | null
  assessments?: Assessment[] | null
}

export type Assessment = {
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  typeCode?: string | null
  statusCode?: string | null
  statusDescription?: string | null
  remainingMinutesEstimation?: number | null
  customQuery?: string | null
  customQueryJson?: string | null
  totalSegmentCount?: number | null
  totalCalculationsCount?: number | null
  counterState?: number | null
  savedStateId?: number | null
  isPublished?: boolean
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  orchestratorResponseUrl?: string | null
  baselineId?: number | null
  isDeleted?: boolean | null
  assessmentClassLocations?: AssessmentClassLocation[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevel[] | null
  assessmentContinuousRoutes?: AssessmentContinuousRoute[] | null
  assessmentRiskLevels?: AssessmentRiskLevel[] | null
  assessmentRiskModels?: AssessmentRiskModel[] | null
  assessmentScenarios?: AssessmentScenario[] | null
  assetCalculationGroups?: AssetCalculationGroup[] | null
  riskResultWis?: RiskResultWis[] | null
  riskResults?: RiskResult[] | null
  riskTotalByAssetWis?: RiskTotalByAssetWis[] | null
  riskTotalByAssets?: RiskTotalByAsset[] | null
  riskTotalByFailureScenarioWis?: RiskTotalByFailureScenarioWis[] | null
  riskTotalByFailureScenarios?: RiskTotalByFailureScenario[] | null
  riskTotalByImpactWis?: RiskTotalByImpactWis[] | null
  riskTotalByImpacts?: RiskTotalByImpact[] | null
  riskTotalBySegmentWis?: RiskTotalBySegmentWis[] | null
  riskTotalBySegments?: RiskTotalBySegment[] | null
  riskTotalByThreatWis?: RiskTotalByThreatWis[] | null
  riskTotalByThreats?: RiskTotalByThreat[] | null
  riskTotalDensityByImpactWis?: RiskTotalDensityByImpactWis[] | null
  riskTotalDensityByImpacts?: RiskTotalDensityByImpact[] | null
  riskTotalDensityByThreatWis?: RiskTotalDensityByThreatWis[] | null
  riskTotalDensityByThreats?: RiskTotalDensityByThreat[] | null
  savedState?: SavedState
  statusCodeNavigation?: AssessmentStatus
  typeCodeNavigation?: AssessmentType
}

export type AssessmentRead = {
  id?: number
  name?: string | null
  userId?: string
  fromForecastYear?: number
  toForecastYear?: number
  typeCode?: string | null
  statusCode?: string | null
  statusDescription?: string | null
  remainingMinutesEstimation?: number | null
  customQuery?: string | null
  customQueryJson?: string | null
  totalSegmentCount?: number | null
  totalCalculationsCount?: number | null
  counterState?: number | null
  savedStateId?: number | null
  isPublished?: boolean
  dateCreated?: string
  dateModified?: string
  createdBy?: string
  modifiedBy?: string
  orchestratorResponseUrl?: string | null
  baselineId?: number | null
  isDeleted?: boolean | null
  assessmentClassLocations?: AssessmentClassLocationRead[] | null
  assessmentConsequenceLevels?: AssessmentConsequenceLevelRead[] | null
  assessmentContinuousRoutes?: AssessmentContinuousRoute[] | null
  assessmentRiskLevels?: AssessmentRiskLevelRead[] | null
  assessmentRiskModels?: AssessmentRiskModelRead[] | null
  assessmentScenarios?: AssessmentScenarioRead[] | null
  assetCalculationGroups?: AssetCalculationGroupRead[] | null
  riskResultWis?: RiskResultWisRead[] | null
  riskResults?: RiskResultRead[] | null
  riskTotalByAssetWis?: RiskTotalByAssetWisRead[] | null
  riskTotalByAssets?: RiskTotalByAssetRead[] | null
  riskTotalByFailureScenarioWis?: RiskTotalByFailureScenarioWisRead[] | null
  riskTotalByFailureScenarios?: RiskTotalByFailureScenarioRead[] | null
  riskTotalByImpactWis?: RiskTotalByImpactWisRead[] | null
  riskTotalByImpacts?: RiskTotalByImpactRead[] | null
  riskTotalBySegmentWis?: RiskTotalBySegmentWisRead[] | null
  riskTotalBySegments?: RiskTotalBySegmentRead[] | null
  riskTotalByThreatWis?: RiskTotalByThreatWisRead[] | null
  riskTotalByThreats?: RiskTotalByThreatRead[] | null
  riskTotalDensityByImpactWis?: RiskTotalDensityByImpactWisRead[] | null
  riskTotalDensityByImpacts?: RiskTotalDensityByImpactRead[] | null
  riskTotalDensityByThreatWis?: RiskTotalDensityByThreatWisRead[] | null
  riskTotalDensityByThreats?: RiskTotalDensityByThreatRead[] | null
  savedState?: SavedStateRead
  statusCodeNavigation?: AssessmentStatusRead
  typeCodeNavigation?: AssessmentTypeRead
}

export type AssessmentContinuousRoute = {
  id?: number
  assessmentId?: number
  fromMeasure?: number
  toMeasure?: number
  continuousRouteId?: number | null
  dateCreated?: string
  dateModified?: string
  assessment?: Assessment
  continuousRoute?: ContinuousRoute
}

export type AssessmentContinuousRouteRead = {
  id?: number
  assessmentId?: number
  fromMeasure?: number
  toMeasure?: number
  continuousRouteId?: number | null
  dateCreated?: string
  dateModified?: string
  assessment?: AssessmentRead
  continuousRoute?: ContinuousRoute
}

export type CalibrationPoint = {
  id?: number
  measure?: number
  fromDate?: string
  toDate?: string | null
  engineeringRouteId?: number
  networkId?: number
  geom?: Geometry
  engineeringRoute?: EngineeringRoute
}

export type CalibrationPointRead = {
  id?: number
  measure?: number
  fromDate?: string
  toDate?: string | null
  engineeringRouteId?: number
  networkId?: number
  geom?: GeometryRead
  engineeringRoute?: EngineeringRoute
}

export type Centerline = {
  id?: number
  geom?: Geometry
  fromMeasure?: number
  toMeasure?: number
  length?: number
  fromDate?: string
  toDate?: string | null
  engineeringRouteId?: number
  engineeringRoute?: EngineeringRoute
}

export type CenterlineRead = {
  id?: number
  geom?: GeometryRead
  fromMeasure?: number
  toMeasure?: number
  length?: number
  fromDate?: string
  toDate?: string | null
  engineeringRouteId?: number
  engineeringRoute?: EngineeringRoute
}

export type EngineeringRoute = {
  id?: number
  name?: string | null
  crFromMeasure?: number
  crToMeasure?: number
  erFromMeasure?: number
  erToMeasure?: number
  offset?: number
  direction?: number
  fromDate?: string
  toDate?: string | null
  continuousRouteId?: number
  calibrationPoints?: CalibrationPoint[] | null
  centerlines?: Centerline[] | null
  continuousRoute?: ContinuousRoute
}

export type EngineeringRouteRead = {
  id?: number
  name?: string | null
  crFromMeasure?: number
  crToMeasure?: number
  erFromMeasure?: number
  erToMeasure?: number
  offset?: number
  direction?: number
  fromDate?: string
  toDate?: string | null
  continuousRouteId?: number
  calibrationPoints?: CalibrationPointRead[] | null
  centerlines?: CenterlineRead[] | null
  continuousRoute?: ContinuousRoute
}

export type GasSystem = {
  id?: number
  name?: string | null
  pipelineRoutes?: PipelineRoute[] | null
}

export type PipelineRoute = {
  id?: number
  name?: string | null
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  gasSystemId?: number
  continuousRoutes?: ContinuousRoute[] | null
  gasSystem?: GasSystem
}

export type PipelineRouteRead = {
  id?: number
  name?: string | null
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  gasSystemId?: number
  continuousRoutes?: ContinuousRoute[] | null
  gasSystem?: GasSystem
}

export type ContinuousRoute = {
  id?: number
  name?: string | null
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  pipelineRouteId?: number
  assessmentContinuousRoutes?: AssessmentContinuousRoute[] | null
  assets?: Asset[] | null
  engineeringRoutes?: EngineeringRoute[] | null
  phmsaSegments?: PhmsaSegment[] | null
  pipelineRoute?: PipelineRoute
  savedStateContinuousRoutes?: SavedStateContinuousRoute[] | null
  segments?: Segment[] | null
  systemScopeContinuousRoutes?: SystemScopeContinuousRoute[] | null
}

export type ContinuousRouteRead = {
  id?: number
  name?: string | null
  fromMeasure?: number
  toMeasure?: number
  fromDate?: string
  toDate?: string | null
  pipelineRouteId?: number
  assessmentContinuousRoutes?: AssessmentContinuousRouteRead[] | null
  assets?: AssetRead[] | null
  engineeringRoutes?: EngineeringRouteRead[] | null
  phmsaSegments?: PhmsaSegmentRead[] | null
  pipelineRoute?: PipelineRouteRead
  savedStateContinuousRoutes?: SavedStateContinuousRouteRead[] | null
  segments?: SegmentRead[] | null
  systemScopeContinuousRoutes?: SystemScopeContinuousRouteRead[] | null
}

export type CreateSystemScopeCommand = {
  janaId?: string
  esriToken?: string | null
  name?: string | null
  isPrivate?: boolean
  isActive?: boolean
  isDefault?: boolean
  customQuery?: string | null
  customQueryString?: string | null
  userId?: string
  continuousRoutes?: ContinuousRoute[] | null
  operators?: OperatorType[] | null
  states?: StateType[] | null
  regions?: RegionType[] | null
}

export type CreateSystemScopeCommandRead = {
  janaId?: string
  esriToken?: string | null
  name?: string | null
  isPrivate?: boolean
  isActive?: boolean
  isDefault?: boolean
  customQuery?: string | null
  customQueryString?: string | null
  userId?: string
  continuousRoutes?: ContinuousRouteRead[] | null
  operators?: OperatorTypeRead[] | null
  states?: StateTypeRead[] | null
  regions?: RegionTypeRead[] | null
}

export type UpdateSystemScopeCommand = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  isPrivate?: boolean
  isActive?: boolean
  isDefault?: boolean
  customQuery?: string | null
  customQueryString?: string | null
  userId?: string
  continuousRoutes?: ContinuousRoute[] | null
  operators?: OperatorType[] | null
  states?: StateType[] | null
  regions?: RegionType[] | null
}

export type UpdateSystemScopeCommandRead = {
  janaId?: string
  esriToken?: string | null
  id?: number
  name?: string | null
  isPrivate?: boolean
  isActive?: boolean
  isDefault?: boolean
  customQuery?: string | null
  customQueryString?: string | null
  userId?: string
  continuousRoutes?: ContinuousRouteRead[] | null
  operators?: OperatorTypeRead[] | null
  states?: StateTypeRead[] | null
  regions?: RegionTypeRead[] | null
}

export type UpdateSystemScopeUserCommand = {
  janaId?: string
  esriToken?: string | null
  systemScopeId?: number
  userId?: string
}

export const {
  usePostApiAssessmentsMutation,
  useGetApiAssessmentsQuery,
  usePatchApiAssessmentsMutation,
  useGetApiAssessmentsByIdQuery,
  useDeleteApiAssessmentsByIdMutation,
  usePutApiAssessmentsByIdMutation,
  useDeleteApiAssessmentsExcludeArcServerByIdMutation,
  usePostApiAssessmentsTerminateByIdMutation,
  usePostApiAssessmentsFilteredSegmentListMutation,
  usePostApiAssessmentScenariosMutation,
  useGetApiAssessmentScenariosQuery,
  useGetApiAssessmentScenariosByIdQuery,
  usePutApiAssessmentScenariosByIdMutation,
  useGetApiAssessmentScenariosScenarioDetailQuery,
  useGetApiAssessmentScenariosScenarioQuery,
  useGetApiAssessmentScenariosAttributeEnumQuery,
  useGetApiBackgroundprocessesByExecutionIdStatusQuery,
  usePostApiBackgroundprocessesDataupdateMutation,
  usePostApiBackgroundprocessesPhmsaSegmentationUpdateMutation,
  usePostApiBackgroundprocessesLrsGeommetryMutation,
  useGetApiBaselineQuery,
  useGetApiBaselineByIdQuery,
  useGetApiBaselineCurrentQuery,
  usePutApiBaselinePublishByIdMutation,
  usePostApiProcessesRecalculatebaselineMutation,
  useGetApiBasesettingsQuery,
  usePostApiClasslocationMutation,
  usePostApiClasslocationExportMutation,
  useGetApiClasslocationAssessmentsQuery,
  useGetApiClasslocationAssessmentsByIdQuery,
  useGetApiClasslocationAssessmentsCurrentQuery,
  usePostApiColourbyvalueMutation,
  usePutApiColourbyvalueMutation,
  useGetApiColourbyvalueByIdQuery,
  usePostApiConsequenceareaMutation,
  usePostApiConsequenceareaExportMutation,
  useGetApiConsequenceareaAssessmentsQuery,
  useGetApiConsequenceareaAssessmentsByIdQuery,
  useGetApiConsequenceareaAssessmentsCurrentQuery,
  useGetApiDatamanagementCustomviewsQuery,
  usePostApiDatamanagementCustomviewsMutation,
  usePutApiDatamanagementCustomviewsByIdMutation,
  useGetApiDatamanagementCustomviewsByIdQuery,
  useDeleteApiDatamanagementCustomviewsByIdMutation,
  useGetApiDatamanagementTableattributesQuery,
  usePostApiDatamanagementExportEventtableMutation,
  usePostApiDatamanagementExportCodeguideMutation,
  usePostApiDatamanagementImportfilesMutation,
  usePostApiDatamanagementImportCommitMutation,
  useGetApiDatamanagementDownloadCsvfileQuery,
  useGetApiDatamanagementImportsQuery,
  useGetApiDatamanagementImportdetailsByIdQuery,
  usePostApiDatamanagementDataviewByIdMutation,
  useGetApiDatamanagementColumndefinitionByIdQuery,
  useGetApiDataupdateEventsAttributesQuery,
  useGetApiEsriEsriTokenQuery,
  useGetApiGassystemsQuery,
  useGetApiGassystemsContinousRouteQuery,
  useGetApiGassystemsByIdQuery,
  useGetApiGassystemsContinuousrouteByIdQuery,
  usePostApiGassystemsContinuousrouteMutation,
  useGetApiGroupTypesQuery,
  useGetApiJobsByIdQuery,
  useGetApiJobsByIdStatusQuery,
  usePatchApiJobsByIdStatusMutation,
  useGetApiOperatorsQuery,
  useGetApiOperatorsByIdQuery,
  usePostApiPhmsaExportMutation,
  usePostApiQraDatatableMutation,
  usePostApiQraDatatableExportMutation,
  usePostApiQraDrilldownMutation,
  usePostApiQraGraphsMutation,
  useGetApiRegionsQuery,
  usePostApiQraSavedstatesMutation,
  useGetApiQraSavedstatesQuery,
  useGetApiQraSavedstatesByIdQuery,
  usePutApiQraSavedstatesByIdMutation,
  useGetApiStatesQuery,
  useGetApiSystemscopesQuery,
  usePostApiSystemscopesMutation,
  usePutApiSystemscopesMutation,
  useGetApiSystemscopesActiveQuery,
  usePutApiSystemscopesActiveMutation,
  useGetApiSystemscopesByIdQuery,
  useDeleteApiSystemscopesByIdMutation,
  useGetApiUsersGetCurrentUserIdQuery
} = injectedRtkApi
